// src/pages/materias/laboral/LaboralExpedienteDetallePage.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { notify } from "../../components/ui/notify";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Typography, Space, Button, Card, ConfigProvider, Input, Select, Checkbox, InputNumber, Modal, Switch } from "antd";
import { HappyProvider } from "@ant-design/happy-work-theme";
import ScrollDatePicker from "../../components/calendar/ScrollDatePicker";
import dayjs from "dayjs";
import { showInsetEffect } from "../../utils/antdInsetWave";
import TabExpedienteActuaciones from "../../components/tribunal/TabExpedienteActuaciones";
import TabDocumentosPruebas from "../../components/tribunal/TabDocumentosPruebas";
import TabCuantificaciones from "../../components/tribunal/TabCuantificaciones";
import TabTerminacion from "../../components/tribunal/TabTerminacion";

import "../../assets/css/LaboralExpedienteDetallePage.css";
import "../../assets/css/LaboralExpedientePruebasSection.css";
// Mismas clases (laboral-exp-*) que usa el header de Conciliación, para que
// el header de Tribunal sea el mismo código, no una copia aparte.
import "../../components/conciliacion/detalles/LaboralExpedienteDetallePage.css";
import { actionTribunalGetById, actionTribunalDelete, actionTribunalUpdateById, actionAuditLogGet as actionTribunalAuditLogGet } from "../../redux/actions/tribunal/tribunal";
import { actionAutoridadesTribunalGet } from "../../redux/actions/autoridades/autoridades";
import useLaboralCatalogos from "./materias/laboral/useLaboralCatalogos";

// ✅ TRIBUNAL components (los sigues usando en su tab)
import DocumentosTribunalCard from "../../components/tribunal/DocumentosTribunalCard";
import MovimientosCard from "../../components/conciliacion/Movimientos";
import { DataBadge } from "../../components/ui";
import { usePermisos } from "../../context/PermisosContext";
import { computeTribunalEstatus, STATUS_PILL_HEX, getTerminosCriticos, getProximaAudienciaTribunal } from "./materias/laboral/tribunalEstatus.js";

// id_modulo con el que el backend guarda el audit_log de Tribunal (ver
// app/models/tribunal.py: create_tribunal_conn / update_tribunal_conn).
const ID_MODULO_TRIBUNAL = 7;

const { Title, Text } = Typography;

const safeObj = (v) => (v && typeof v === "object" ? v : {});
const safeArr = (v) => (Array.isArray(v) ? v : []);

const fmtDateLong = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
};

const fmtShort = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const day = d.toLocaleDateString("es-ES", { day: "2-digit" });
  const mon = d.toLocaleDateString("es-ES", { month: "short" }).replace(".", "");
  const yr = d.toLocaleDateString("es-ES", { year: "numeric" });
  return `${day} ${mon} ${yr}`.replace(" de ", " ");
};

const toTitleCase = (str) => {
  if (!str || typeof str !== "string") return "—";
  return str
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};
const formatTipoList = (arr) => {
  const items = safeArr(arr).filter(Boolean);
  if (!items.length) return "—";
  return items.map((it) => toTitleCase(String(it).replaceAll("_", " "))).join(", ");
};

// Mismas opciones que usa AccionesPrestacionesSection.jsx en el
// formulario completo de creación/edición, para que la edición inline
// de esta tarjeta grabe exactamente los mismos valores.
const ACCION_INTENTADA_OPTIONS = [
  { label: "DESPIDO", value: "DESPIDO" },
  { label: "RESCISION LABORAL TRABAJADOR", value: "RESCISION LABORAL TRABAJADOR" },
  { label: "REINSTALACION", value: "REINSTALACION" },
  { label: "PAGO DE PRESTACIONES", value: "PAGO DE PRESTACIONES" },
  { label: "DERECHO DE PREFERENCIA Y/O ASCENSO", value: "DERECHO DE PREFERENCIA Y/O ASCENSO" },
];

// Mismas opciones que usa CondicionesTrabajoSection.jsx en el formulario
// completo, para que la edición inline grabe los mismos valores.
const DIA_DESCANSO_OPTIONS = [
  { label: "Un dia a la semana", value: "Un dia a la semana" },
  { label: "Sin descanso", value: "Sin descanso" },
  { label: "Lunes", value: "Lunes" },
  { label: "Martes", value: "Martes" },
  { label: "Miércoles", value: "Miércoles" },
  { label: "Jueves", value: "Jueves" },
  { label: "Viernes", value: "Viernes" },
  { label: "Sábado", value: "Sábado" },
  { label: "Domingo", value: "Domingo" },
];

// Mismas opciones que usa PruebasSection.jsx en el formulario completo.
const PERICIAL_TIPO_OPTIONS = [
  { label: "Caligráfica-Grafoscópica", value: "caligrafica_grafoscopica" },
  { label: "Fonográfica", value: "fonografica" },
  { label: "Contable", value: "contable" },
  { label: "Medios Electrónicos - Informática", value: "medios_electronicos_informatica" },
  { label: "Psicológica", value: "psicologica" },
  { label: "Médica", value: "medica" },
  { label: "Seguridad e Higiene", value: "seguridad_e_higiene" },
  { label: "Valuación", value: "valuacion" },
  { label: "Otros", value: "otros" },
];

const INSPECCION_OCULAR_TIPO_OPTIONS = [
  { label: "Recibos de pago", value: "recibos_pago" },
  { label: "Nóminas", value: "nominas" },
  { label: "Control de asistencia", value: "control_asistencia" },
  { label: "Tarjetas", value: "tarjetas" },
  { label: "Altas y bajas al seguro social", value: "altas_bajas_seguro_social" },
];

const MEDIOS_ELECTRONICOS_OPTIONS = [
  { label: "Fotografías", value: "fotografias" },
  { label: "Video (CCTV / celular)", value: "video" },
  { label: "Audio", value: "audio" },
  { label: "Mensajes (WhatsApp / SMS)", value: "mensajes" },
  { label: "Correos electrónicos", value: "emails" },
  { label: "Registros biométricos", value: "biometricos" },
  { label: "Geolocalización (GPS)", value: "geolocalizacion" },
  { label: "Web / Redes sociales", value: "web_redes" },
];

const PRESTACIONES_OPTIONS = [
  ["prima_antiguedad", "Prima de Antigüedad"],
  ["salarios_caidos", "Salarios Vencidos"],
  ["vacaciones", "Vacaciones"],
  ["prima_vacacional", "Prima Vacacional"],
  ["aguinaldo", "Aguinaldo"],
  ["reparto_utilidades", "Reparto de Utilidades"],
  ["horas_extras", "Horas Extras"],
  ["dias_90_salario", "Indemnización Const. (90 DÍAS DE SALARIO)"],
  ["dias_20_salario", "Indemnización art. 50 (20 días por año)"],
  ["reinstalacion", "Reinstalacion"],
  ["comisiones", "Comisiones"],
  ["incripcion_retroactiva_imss", "Incripcion Retroactiva al IMSS"],
];

// Mismo patrón "label arriba / valor abajo" que usa la tarjeta "Datos del
// expediente" de Conciliación (laboral-exp-field-grid), para que ambas
// pantallas compartan estilo, iconos y tamaños de texto sin duplicar CSS.
function Field({ label, value, span2 = false }) {
  return (
    <div className={`laboral-exp-field${span2 ? " laboral-exp-field--span2" : ""}`}>
      <span className="laboral-exp-field-label">{label}</span>
      <span className="laboral-exp-field-value">{value ?? "—"}</span>
    </div>
  );
}
export default function LaboralExpedienteDetallePage({ maps = {} }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { idExpediente } = useParams();
  const location = useLocation();

  const { perm, canUseCity } = usePermisos() || { perm: () => true, canUseCity: () => true };

  const [activeMainTab, setActiveMainTab] = useState("2");
  const [deletingExp, setDeletingExp] = useState(false);
  const [auditItems, setAuditItems] = useState([]);

  useEffect(() => {
    if (!idExpediente) return;
    dispatch(actionTribunalAuditLogGet({ id_modulo: ID_MODULO_TRIBUNAL, id_key: idExpediente, limit: 100 }))
      .then((data) => setAuditItems(data?.items || []))
      .catch(() => setAuditItems([]));
  }, [dispatch, idExpediente]);

  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(true);

  const puedeUsarCiudadExpediente = canUseCity("tribunal", detalle?.id_ciudad);
  const canEliminar = perm("tribunal", "eliminar") && puedeUsarCiudadExpediente;

  // ============================================================
  // Edición inline (sin página aparte) — mismo patrón que
  // LaboralConciliacionDetallePage: click en Editar convierte la
  // tarjeta "Datos del Expediente" en un formulario in-place.
  // ============================================================
  const [editingDatosCard, setEditingDatosCard] = useState(false);
  const [editingDatosForm, setEditingDatosForm] = useState({});
  const [savingDatosCard, setSavingDatosCard] = useState(false);
  const [selectedEstadoId, setSelectedEstadoId] = useState(null);
  const [selectedCiudadId, setSelectedCiudadId] = useState(null);
  const [selectedAutoridadId, setSelectedAutoridadId] = useState(null);

  // ── Carga rápida (IA): pegar JSON extraído de la demanda, revisar y guardar ──
  const [cargaRapidaOpen, setCargaRapidaOpen] = useState(false);
  const [cargaRapidaText, setCargaRapidaText] = useState("");
  const [cargaRapidaError, setCargaRapidaError] = useState("");
  const [cargaRapidaReview, setCargaRapidaReview] = useState(null); // { payload, cambios }
  const [cargaRapidaSaving, setCargaRapidaSaving] = useState(false);

  // Edición inline — tarjeta "Acciones y prestaciones"
  const [editingAccionesCard, setEditingAccionesCard] = useState(false);
  const [editingAccionesForm, setEditingAccionesForm] = useState({});
  const [savingAccionesCard, setSavingAccionesCard] = useState(false);

  // Edición inline — tarjeta "Hechos y Narrativa"
  const [editingHechosCard, setEditingHechosCard] = useState(false);
  const [editingHechosForm, setEditingHechosForm] = useState({});
  const [savingHechosCard, setSavingHechosCard] = useState(false);

  // Edición inline — tarjeta "Condiciones de Trabajo"
  const [editingCondicionesCard, setEditingCondicionesCard] = useState(false);
  const [editingCondicionesForm, setEditingCondicionesForm] = useState({});
  const [savingCondicionesCard, setSavingCondicionesCard] = useState(false);

  // Edición inline — tarjeta "Testimonial"
  const [editingTestimonialCard, setEditingTestimonialCard] = useState(false);
  const [editingTestimonialForm, setEditingTestimonialForm] = useState([]);
  const [savingTestimonialCard, setSavingTestimonialCard] = useState(false);

  // Edición inline — tarjeta "Documental Pública"
  const [editingDocPublicaCard, setEditingDocPublicaCard] = useState(false);
  const [editingDocPublicaForm, setEditingDocPublicaForm] = useState([]);
  const [savingDocPublicaCard, setSavingDocPublicaCard] = useState(false);

  // Edición inline — tarjeta "Documental Privada"
  const [editingDocPrivadaCard, setEditingDocPrivadaCard] = useState(false);
  const [editingDocPrivadaForm, setEditingDocPrivadaForm] = useState([]);
  const [savingDocPrivadaCard, setSavingDocPrivadaCard] = useState(false);

  // Edición inline — tarjeta "Pericial"
  const [editingPericialCard, setEditingPericialCard] = useState(false);
  const [editingPericialForm, setEditingPericialForm] = useState([]);
  const [savingPericialCard, setSavingPericialCard] = useState(false);

  // Edición inline — tarjeta "Inspección Ocular"
  const [editingInspeccionCard, setEditingInspeccionCard] = useState(false);
  const [editingInspeccionForm, setEditingInspeccionForm] = useState([]);
  const [savingInspeccionCard, setSavingInspeccionCard] = useState(false);

  // Switches de estatus (Instrumental / Presuncional / Confesional Expresa)
  const [savingChecklistKey, setSavingChecklistKey] = useState(null);

  // Edición inline — tarjeta "Confesional"
  const [editingConfesionalCard, setEditingConfesionalCard] = useState(false);
  const [editingConfesionalForm, setEditingConfesionalForm] = useState([]);
  const [savingConfesionalCard, setSavingConfesionalCard] = useState(false);

  // Edición inline — tarjeta "Confesional de Hechos Propios"
  const [editingConfesionalPropiosCard, setEditingConfesionalPropiosCard] = useState(false);
  const [editingConfesionalPropiosForm, setEditingConfesionalPropiosForm] = useState([]);
  const [savingConfesionalPropiosCard, setSavingConfesionalPropiosCard] = useState(false);

  // Edición inline — tarjeta "Confesional Demandada"
  const [editingConfesionalDemandadaCard, setEditingConfesionalDemandadaCard] = useState(false);
  const [editingConfesionalDemandadaForm, setEditingConfesionalDemandadaForm] = useState([]);
  const [savingConfesionalDemandadaCard, setSavingConfesionalDemandadaCard] = useState(false);

  // Edición inline — tarjeta "Informe de Autoridad"
  const [editingInformeAutoridadCard, setEditingInformeAutoridadCard] = useState(false);
  const [editingInformeAutoridadForm, setEditingInformeAutoridadForm] = useState([]);
  const [savingInformeAutoridadCard, setSavingInformeAutoridadCard] = useState(false);

  // Edición inline — tarjeta "Medios Electrónicos / Ciencia"
  const [editingMediosCard, setEditingMediosCard] = useState(false);
  const [editingMediosForm, setEditingMediosForm] = useState({ tipos: [], descripcion: "" });
  const [savingMediosCard, setSavingMediosCard] = useState(false);

  const {
    estadosOptions,
    ciudadesOptions: ciudadesOptionsAll,
    ciudadesById: ciudadesByIdCatalogo,
  } = useLaboralCatalogos(selectedEstadoId, selectedCiudadId, { modulo: "tribunal" });

  const autoridadesSlice = useSelector((state) => state?.autoridades ?? {});
  useEffect(() => {
    dispatch(actionAutoridadesTribunalGet());
  }, [dispatch]);

  const coerceItems = (slice) => {
    if (!slice) return [];
    const data = slice.data ?? slice;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    return [];
  };

  const autoridadesItems = useMemo(() => coerceItems(autoridadesSlice), [autoridadesSlice]);

  const ciudadIdsConAutoridad = useMemo(() => {
    const s = new Set();
    autoridadesItems.forEach((au) => {
      if (au?.id_ciudad != null && String(au.id_ciudad) !== "") s.add(Number(au.id_ciudad));
    });
    return s;
  }, [autoridadesItems]);

  const estadoIdsConAutoridad = useMemo(() => {
    const s = new Set();
    ciudadIdsConAutoridad.forEach((idc) => {
      const c = ciudadesByIdCatalogo?.[idc] || ciudadesByIdCatalogo?.[String(idc)];
      const idEstado = c?.id_estado;
      if (idEstado != null) s.add(Number(idEstado));
    });
    return s;
  }, [ciudadIdsConAutoridad, ciudadesByIdCatalogo]);

  const estadosOptionsFinal = useMemo(
    () => (estadosOptions || []).filter((o) => estadoIdsConAutoridad.has(Number(o.value))),
    [estadosOptions, estadoIdsConAutoridad]
  );

  const ciudadesOptionsFinal = useMemo(() => {
    if (!selectedEstadoId) return [];
    return (ciudadesOptionsAll || []).filter((o) => ciudadIdsConAutoridad.has(Number(o.value)));
  }, [ciudadesOptionsAll, ciudadIdsConAutoridad, selectedEstadoId]);

  const autoridadOptions = useMemo(() => {
    if (!selectedCiudadId) return [];
    return autoridadesItems
      .filter((au) => Number(au?.id_ciudad || 0) === Number(selectedCiudadId))
      .map((au) => ({ label: au?.nombre || "", value: au?.id }))
      .filter((o) => o.label);
  }, [autoridadesItems, selectedCiudadId]);

  const fetchDetalle = useCallback(async () => {
    const data = await dispatch(actionTribunalGetById(idExpediente));
    const d =
      data?.detalle ||
      data?.data ||
      data?.item ||
      (Array.isArray(data?.items) ? data.items[0] : null) ||
      data;
    setDetalle(d && typeof d === "object" ? d : null);
    return d;
  }, [dispatch, idExpediente]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoadingDetalle(true);
        await fetchDetalle();
      } catch (e) {
        if (!mounted) return;
        setDetalle(null);
        notify.error({
          message: "Error al cargar expediente",
          description: e?.response?.data?.detail || e?.message || "No se pudo cargar el expediente.",
        });
      } finally {
        if (!mounted) return;
        setLoadingDetalle(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [fetchDetalle]);


  const mapsSafe = safeObj(maps);
  const ciudadesById = safeObj(mapsSafe.ciudadesById);
  const estadosById = safeObj(mapsSafe.estadosById);
  const isFlat = detalle && typeof detalle === "object" && !detalle?.datos_generales;


  const meta = safeObj(detalle?.meta);

  const dg = isFlat
    ? {
        estado: detalle?.id_estado ?? null,
        ciudad: detalle?.id_ciudad ?? null,
        autoridad: detalle?.id_autoridad ?? null,
        num_unico: detalle?.expediente ?? null,
        numero_expediente: detalle?.numero_expediente ?? "",
      }
    : safeObj(detalle?.datos_generales);

  const ap = isFlat
    ? {
        trabajador_nombre: detalle?.nombre_trabajador ?? "",
        accion_intentada: detalle?.accion_intentada ?? "",
        fecha_recepcion_demanda: detalle?.fecha_recepcion_demanda ?? null,
        otras_prestaciones: detalle?.otras_prestaciones ?? "",
        prestaciones: safeObj(detalle?.prestaciones),
      }
    : safeObj(detalle?.acciones_prestaciones);

  const hechos = isFlat
    ? {
        responsable_despido: detalle?.responsable_despido ?? "",
        jefes_inmediatos: detalle?.jefes_inmediatos ?? "",
        narrativa: detalle?.narrativa ?? "",
      }
    : safeObj(detalle?.hechos);
   
const ct = isFlat
  ? {
      fecha_ingreso: detalle?.fecha_ingreso ?? null,
      puesto: detalle?.puesto ?? "",
      salario_diario: detalle?.salario_diario ?? "",
      salario_diario_integrado:
        detalle?.salario_diario_integrado ??
        detalle?.debug_flat?.salario_diario_integrado ??
        "",
      lugar_servicio: detalle?.lugar_servicio ?? "",
      sdi: detalle?.debug_flat?.salario_diario_integrado ?? "",
      jornada: detalle?.jornada ?? "",
      dia_descanso: detalle?.dia_descanso ?? "",
    }
  : safeObj(detalle?.condiciones_trabajo);
  const etapa = isFlat
    ? {
        fecha_limite_contestacion: detalle?.fecha_limite_contestacion ?? null,
        fecha_audiencia_conciliatoria: detalle?.fecha_audiencia_conciliadora ?? detalle?.fecha_audiencia_conciliatoria ?? null,
        observaciones_etapa_actual: detalle?.observaciones_etapa_inicial ?? "",
        id_tribunal_documento_citatorio: detalle?.id_tribunal_documento_citatorio ?? null,
        citatorio: safeObj(detalle?.citatorio),
        fecha_presentacion_demanda: detalle?.fecha_presentacion_demanda ?? null,
        fecha_notificacion_demanda: detalle?.fecha_notificacion_demanda ?? null,
        fecha_ultimo_dia_laborado: detalle?.fecha_ultimo_dia_laborado ?? null,
      }
    : safeObj(detalle?.etapa_procesal);

  const pruebas = isFlat ? {} : safeObj(detalle?.pruebas_documentacion);

  const ciudadName =
    dg?.nombre_ciudad ||
    detalle?.nombre_ciudad ||
    ciudadesById?.[dg?.ciudad]?.nombre ||
    ciudadesById?.[dg?.ciudad] ||
    (dg?.ciudad ? `#${dg.ciudad}` : "—");

  const estadoName =
    dg?.nombre_estado ||
    detalle?.nombre_estado ||
    estadosById?.[dg?.estado]?.nombre ||
    estadosById?.[dg?.estado] ||
    (dg?.estado ? `#${dg.estado}` : "—");

  const autoridadName =
    dg?.nombre_autoridad ||
    detalle?.nombre_autoridad ||
    (dg?.autoridad ? `#${dg.autoridad}` : "—");

  const rightSideTitle = detalle?.nombre_autoridad || ap?.accion_intentada || "—";
  const statusTag = Number(detalle?.active ?? 0) === 1 ? "Activo" : "Inactivo";

  const demandadaTitle = useMemo(() => {
    const rs = safeArr(dg?.razones_sociales);
    const names = rs
      .map((x) => String(x?.razon_social || "").trim())
      .filter(Boolean);

    return names.length ? names.join(" vs ") : "—";
  }, [dg]);

  const prestaciones = useMemo(() => {
    const p = safeObj(ap?.prestaciones);
    return Object.keys(p).filter((k) => p[k] === true);
  }, [ap]);

  const razonesSocialesDemandadaOptions = useMemo(() => {
    return safeArr(dg?.razones_sociales)
      .filter((rs) => rs?.id_razon_social != null)
      .map((rs) => ({ label: rs?.razon_social || `Razón social ${rs.id_razon_social}`, value: rs.id_razon_social }));
  }, [dg]);

  const caseInfoConcentrado = useMemo(() => ({
    numeroExpediente: dg?.numero_expediente || "—",
    numeroUnico: dg?.num_unico || "—",
    trabajador: ap?.trabajador_nombre || "—",
    demandada: demandadaTitle,
    autoridad: autoridadName,
    ciudad: ciudadName,
    estado: estadoName,
  }), [dg, ap, demandadaTitle, autoridadName, ciudadName, estadoName]);

  // Plazos críticos (ejemplo: contestación)
  const vencioContestacion = detalle?.etapas_procesales?.fecha_limite_contestacion;
  const diasVencidos = useMemo(() => {
    if (!vencioContestacion) return null;
    const d = new Date(vencioContestacion);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    const diff = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    // si diff > 0 => vencido
    return diff;
  }, [vencioContestacion]);

  const handleDeleteExpediente = async () => {
    const idToDelete = detalle?.id;
    if (!idToDelete) {
      notify.error({ message: "No se puede eliminar", description: "No se encontró el ID del expediente." });
      return;
    }
    if (!window.confirm("El expediente se desactivará y no aparecerá en la lista. Esta acción no se puede deshacer.\n\n¿Eliminar expediente?")) return;
    try {
      setDeletingExp(true);
      await dispatch(actionTribunalDelete(idToDelete));
      notify.success({ message: "Expediente eliminado", description: "El expediente fue desactivado correctamente." });
      navigate("/materias/laboral/tribunal");
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.message || "No se pudo eliminar el expediente.";
      notify.error({ message: "Error al eliminar", description: msg });
    } finally {
      setDeletingExp(false);
    }
  };

  if (loadingDetalle) {
    return (
      <div className="lxp-root">
        <div className="lxp-wrap">
          <div className="lxp-card lxp-summary">
            Cargando expediente...
          </div>
        </div>
      </div>
    );
  }

  if (!detalle) {
    return (
      <div className="lxp-root">
        <div className="lxp-wrap">
          <div className="lxp-card lxp-summary">
            No se encontró el expediente.
          </div>
        </div>
      </div>
    );
  }

  const citFilename = etapa?.citatorio?.filename || (etapa?.citatorio?.url ? "Ver demanda" : "—");
  const citHas = Boolean(etapa?.citatorio?.url);

  // Mismo badge de etapa/estatus que usa el listado de Tribunal
  // (laboralTribunalPage.jsx), para que el header del detalle luzca
  // idéntico al de Conciliación (que también trae un badge junto al título).
  const estatusPill = computeTribunalEstatus(detalle);
  const proximaAudienciaTribunal = getProximaAudienciaTribunal(detalle);
  const terminosCriticosTribunal = getTerminosCriticos(detalle);

const dataActor = safeArr(pruebas?.actor || pruebas?.actora || pruebas?.pruebas_actor || pruebas?.parte_actora);
const dataDemandada = safeArr(pruebas?.demandada || pruebas?.pruebas_demandada || pruebas?.parte_demandada);

const pruebasDetalle = safeObj(pruebas?.detalle);
const pruebasActora = safeObj(pruebasDetalle?.actora);

const confesionalesActora = safeArr(pruebasActora?.confesional);
const confesionalesPropiosActora = safeArr(pruebasActora?.confesional_hechos_propios);

const testimonialActora = safeObj(pruebasActora?.testimonial);
const testigosActora = safeArr(testimonialActora?.testigos);

const documentalPublicaActora = safeObj(pruebasActora?.documental_publica);
const documentosPublicosActora = safeArr(documentalPublicaActora?.documentos);

const documentalPrivadaActora = safeObj(pruebasActora?.documental_privada);
const documentosPrivadosActora = safeArr(documentalPrivadaActora?.documentos);

const pericialActora = safeObj(pruebasActora?.pericial);
const inspeccionOcularActora = safeObj(pruebasActora?.inspeccion_ocular);

const checklistActora = safeObj(pruebas?.checklist?.actora);
const checklistDemandadaGlobal = safeObj(pruebas?.checklist?.demandada);
const tieneConfesionalExpresa = Boolean(checklistActora?.confesional_expresa);
const tieneInstrumentalActora = Boolean(checklistActora?.instrumental_actuaciones);
const tienePresuncionalActora = Boolean(checklistActora?.presuncional_legal_humana);

  // audiencia
  const audIso = etapa?.fecha_audiencia_conciliatoria;
  const audDate = audIso ? new Date(audIso) : null;
  const audMonth = audDate && !Number.isNaN(audDate.getTime())
    ? audDate.toLocaleDateString("es-ES", { month: "short" }).replace(".", "").toUpperCase()
    : "—";
  const audDay = audDate && !Number.isNaN(audDate.getTime())
    ? audDate.toLocaleDateString("es-ES", { day: "2-digit" })
    : "—";

  // fechas clave adicionales
  const fechaPresentacionDemanda = dg?.fecha_presentacion_demanda || detalle?.fecha_presentacion_demanda || null;
  const fechaNotificacionDemanda = dg?.fecha_notificacion_demanda || detalle?.fecha_notificacion_demanda || null;
  const fechaRadicacionDemanda= dg?.fecha_radicacion || detalle?.fecha_radicacion || null;
  const fechaNoConciliacionDemanda = dg?.fecha_no_conciliacion || detalle?.fecha_no_conciliacion || null;
  const fechaEmisionDemanda = dg?.fecha_emision_ccl || detalle?.fecha_emision_ccl || null;
    const fechaLimiteDemanda = dg?.fecha_limite_contestacion || detalle?.fecha_limite_contestacion || null;
  const diasLimiteDemanda = dg?.dias_limite || detalle?.dias_limite || null;
const fechaUltimoDiaLaborado =
  detalle?.etapas_procesales?.fecha_ultimo_dia_laborado ||
  detalle?.fecha_ultimo_dia_laborado ||
  null;
  const pruebasDemandada = safeObj(pruebasDetalle?.demandada);
const confesionalesDemandada = safeArr(pruebasDemandada?.confesional);

const informeAutoridadActora = safeObj(pruebasActora?.informe_autoridad);
const informesAutoridadActora = safeArr(informeAutoridadActora?.documentos);
const mediosElectronicosActora = safeObj(pruebasActora?.medios_electronicos);
// pericial/inspección ocular: el backend guarda cada registro con un "tipo"
// ya unido por " - " (varias opciones elegidas en un solo registro se juntan
// en una sola columna de texto), por eso al editar hay que separarlo de
// vuelta en array para el multi-select.
const splitJoinedTipo = (s) => (s ? String(s).split(" - ").map((x) => x.trim()).filter(Boolean) : []);
const pericialItemsActora = safeArr(pericialActora?.items);
const inspeccionItemsActora = safeArr(inspeccionOcularActora?.items);

// Lista aplanada de las pruebas de la actora (ofrecidas en la demanda) para
// calificarlas (admitida/desechada) junto a las de la demandada en la audiencia
// preliminar — ver TabExpedienteActuaciones.
const pruebasActoraFlat = [
  ...confesionalesActora.map((item) => ({ tipo: "confesional", resumen: item?.persona_confesar || "" })),
  ...confesionalesPropiosActora.map((item) => ({ tipo: "confesional", resumen: item?.tipo_prueba || "Confesional por hechos propios" })),
  ...testigosActora.map((t) => ({ tipo: "testimonial", resumen: t?.nombre || "" })),
  ...documentosPublicosActora.map((d) => ({ tipo: "documental_publica", resumen: d?.nombre || "" })),
  ...documentosPrivadosActora.map((d) => ({ tipo: "documental_privada", resumen: d?.nombre || "" })),
  ...informesAutoridadActora.map((d) => ({ tipo: "informe_autoridad", resumen: d?.nombre || "" })),
  ...pericialItemsActora.map((item) => ({ tipo: "pericial", resumen: [splitJoinedTipo(item?.tipo).join(", "), item?.perito].filter(Boolean).join(" — ") })),
  ...inspeccionItemsActora.map((item) => ({ tipo: "inspeccion_ocular", resumen: [splitJoinedTipo(item?.tipo).join(", "), item?.nombre].filter(Boolean).join(" — ") })),
  ...(safeArr(mediosElectronicosActora?.tipos).length
    ? [{ tipo: "medios_electronicos", resumen: safeArr(mediosElectronicosActora?.tipos).join(", ") }]
    : []),
].filter((p) => p.tipo);

  // etapas_procesales tal cual lo manda el backend (no la versión "etapa"
  // de arriba, que lee la clave singular "etapa_procesal" y por eso
  // siempre sale vacía contra la respuesta real del GET).
  const etapaProcesalReal = safeObj(detalle?.etapas_procesales);
  const fechaLimiteContestacionActual = etapaProcesalReal?.fecha_limite_contestacion || fechaLimiteDemanda || null;

  // ============================================================
  // Edición inline de "pruebas_documentacion" — el GET y el PUT usan
  // nombres de campo distintos para pericial/inspección ocular
  // (tipo/perito/objeto vs. tipo_pericial/nombre_perito/objeto_prueba).
  // Si se reenvía tal cual el bloque que regresa el GET, el backend
  // pierde/vacía esos campos en CADA guardado, aunque la tarjeta editada
  // no toque pruebas. Por eso esta función siempre reconstruye el payload
  // completo con los nombres que espera el PUT, y solo la sección que se
  // está editando se reemplaza por overridesActora/overridesChecklist/
  // overridesDemandada.
  // ============================================================
  const buildPruebasDocumentacionPayload = (
    overridesActora = {},
    overridesChecklist = {},
    overridesDemandada = {}
  ) => {
    const confesionalDefault = safeArr(pruebasActora?.confesional).map((item) => ({
      persona_confesar: item?.persona_confesar || item?.razon_social || "",
      hechos_confesar: item?.hechos_confesar || "",
    }));

    const confesionalPropiosDefault = confesionalesPropiosActora.map((item) => ({
      tipo_prueba: item?.tipo_prueba || "",
      hechos_propios: item?.hechos_propios || "",
    }));

    const confesionalDemandadaDefault = confesionalesDemandada.map((item) => ({
      id_razon_social: item?.id_razon_social ?? null,
    }));

    const pericialDefault = pericialItemsActora.map((item) => ({
      tipo_pericial: splitJoinedTipo(item?.tipo),
      nombre_perito: item?.perito || "",
      objeto_prueba: item?.objeto || "",
    }));

    const inspeccionDefault = inspeccionItemsActora.map((item) => ({
      tipo_inspeccion_ocular: splitJoinedTipo(item?.tipo),
      nombre_inspeccion_ocular: item?.nombre || "",
      objeto_prueba: item?.objeto || "",
    }));

    const testimonialDefault = {
      tipo: safeArr(testimonialActora?.tipo),
      testigos: safeArr(testimonialActora?.testigos).map((t) => ({
        nombre: t?.nombre || "",
        relacion: t?.relacion || "",
        contacto: t?.contacto || "",
      })),
    };

    const documentalPublicaDefault = {
      documentos: safeArr(documentalPublicaActora?.documentos).map((d) => ({
        nombre: d?.nombre || "",
        descripcion: d?.descripcion || "",
        fecha: d?.fecha || "",
      })),
    };

    const documentalPrivadaDefault = {
      documentos: safeArr(documentalPrivadaActora?.documentos).map((d) => ({
        nombre: d?.nombre || "",
        descripcion: d?.descripcion || "",
        fecha: d?.fecha || "",
      })),
    };

    const informeAutoridadDefault = {
      documentos: informesAutoridadActora.map((d) => ({
        nombre: d?.nombre || "",
        descripcion: d?.descripcion || "",
        fecha: d?.fecha || "",
      })),
    };

    return {
      tab: "actora",
      checklist: {
        actora: {
          ...(pruebas?.checklist?.actora || {}),
          ...overridesChecklist,
        },
      },
      detalle: {
        actora: {
          confesional: confesionalDefault,
          confesional_hechos_propios: confesionalPropiosDefault,
          testimonial: testimonialDefault,
          documental_publica: documentalPublicaDefault,
          documental_privada: documentalPrivadaDefault,
          informe_autoridad: informeAutoridadDefault,
          pericial: pericialDefault,
          inspeccion_ocular: inspeccionDefault,
          medios_electronicos: {
            tipos: safeArr(mediosElectronicosActora?.tipos),
            descripcion: mediosElectronicosActora?.descripcion || "",
          },
          ...overridesActora,
        },
        demandada: {
          confesional: confesionalDemandadaDefault,
          ...overridesDemandada,
        },
      },
      evidencias_actora: [],
    };
  };

  // ============================================================
  // Edición inline — arma el payload completo que espera el PUT
  // /tribunal/{id}. El backend reemplaza los campos planos con lo que
  // reciba (y desvincula razones sociales si no vienen), así que hay
  // que reenviar TODAS las secciones con los valores actuales y solo
  // pisar las que esta tarjeta realmente edita.
  // ============================================================
  const buildTribunalBasePayload = (overrides = {}) => {
    const rsIds = safeArr(dg?.razones_sociales)
      .map((r) => r?.id_razon_social)
      .filter((v) => v != null);

    return {
      meta: {
        isEdit: true,
        idExpediente: detalle?.id ?? null,
        idCiudad: dg?.ciudad ? Number(dg.ciudad) : null,
        idAutoridad: dg?.autoridad ? Number(dg.autoridad) : null,
        nombreIdentificacionCiudad: dg?.nombre_ciudad || "",
      },
      datos_generales: {
        numero_expediente: dg?.numero_expediente || null,
        nombre_parte_actora: dg?.nombre_parte_actora || ap?.trabajador_nombre || null,
        empresa_id: dg?.empresa_id ?? null,
        empresa_nombre: dg?.empresa_nombre || "",
        empresa_razon_social_ids: rsIds,
        empresa_new: null,
        razones_sociales_new: [],
        estado: dg?.estado ? Number(dg.estado) : null,
        ciudad: dg?.ciudad ? Number(dg.ciudad) : null,
        autoridad: dg?.autoridad ? Number(dg.autoridad) : null,
        fecha_notificacion_demanda: fechaNotificacionDemanda || null,
        fecha_constancia_conciliacion: fechaNoConciliacionDemanda || null,
        fecha_emision_citatorio: fechaEmisionDemanda || null,
        fecha_radicacion_demanda: fechaRadicacionDemanda || null,
        dias_limite: diasLimiteDemanda ?? null,
        fecha_presentacion_demanda: fechaPresentacionDemanda || null,
        num_unico: dg?.num_unico || null,
        ...overrides.datos_generales,
      },
      etapas_procesales: {
        fecha_limite_contestacion: fechaLimiteContestacionActual,
        fecha_audiencia_conciliatoria: etapaProcesalReal?.fecha_audiencia_conciliatoria || null,
        observaciones_etapa_actual: etapaProcesalReal?.observaciones_etapa_actual || "",
        ...overrides.etapas_procesales,
      },
      acciones_prestaciones: {
        accion_intentada: ap?.accion_intentada || null,
        prestaciones: { ...(ap?.prestaciones || {}) },
        otras_prestaciones: ap?.otras_prestaciones || "",
        ...overrides.acciones_prestaciones,
      },
      hechos: {
        responsable_despido: hechos?.responsable_despido || "",
        jefes_inmediatos: hechos?.jefes_inmediatos || "",
        abogado_contrario: hechos?.abogado_contrario || "",
        narrativa: hechos?.narrativa || "",
        ...overrides.hechos,
      },
      condiciones_trabajo: {
        fecha_ingreso: ct?.fecha_ingreso || null,
        fecha_ultimo_dia_laborado: fechaUltimoDiaLaborado || null,
        puesto: ct?.puesto || "",
        salario_diario: ct?.salario_diario === "" ? null : ct?.salario_diario ?? null,
        salario_diario_integrado: ct?.salario_diario_integrado === "" ? null : ct?.salario_diario_integrado ?? null,
        jornada: ct?.jornada || "",
        dia_descanso: ct?.dia_descanso || "",
        lugar_servicio: ct?.lugar_servicio || "",
        ...overrides.condiciones_trabajo,
      },
      pruebas_documentacion: buildPruebasDocumentacionPayload(
        overrides.pruebasActora,
        overrides.pruebasChecklist,
        overrides.pruebasDemandada
      ),
    };
  };

  // ── Carga rápida (IA) ───────────────────────────────────────────────────
  const normStrCR = (s) =>
    (s || "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .split("")
      .filter((ch) => { const code = ch.charCodeAt(0); return code < 0x0300 || code > 0x036f; })
      .join("")
      .replace(/\s+/g, " ")
      .trim();

  const fuzzyMatchOptionsCR = (needle, options) => {
    if (!needle || !options?.length) return null;
    const n = normStrCR(needle);
    let best = null;
    let bestScore = 0;
    for (const opt of options) {
      const c = normStrCR(opt.label || "");
      if (!c) continue;
      let score = 0;
      if (c === n) score = 100;
      else if (c.startsWith(n) || n.startsWith(c)) score = 80;
      else if (c.includes(n) || n.includes(c)) score = 60;
      else {
        const nw = n.split(" ").filter((w) => w.length > 2);
        const cw = c.split(" ");
        const exact = nw.filter((w) => cw.includes(w)).length;
        if (exact > 0) score = 20 + exact * 10;
      }
      if (score > bestScore) { bestScore = score; best = opt; }
    }
    return bestScore > 0 ? best : null;
  };

  const fmtFechaCR = (v) => {
    if (!v) return "—";
    const d = dayjs(v);
    return d.isValid() ? d.format("DD/MM/YYYY") : String(v);
  };

  const cerrarCargaRapida = () => {
    setCargaRapidaOpen(false);
    setCargaRapidaText("");
    setCargaRapidaError("");
    setCargaRapidaReview(null);
  };

  const parseCargaRapida = () => {
    let parsed;
    try {
      parsed = JSON.parse(cargaRapidaText);
    } catch (e) {
      setCargaRapidaError("JSON inválido: " + e.message);
      return;
    }
    setCargaRapidaError("");

    const dgIn = parsed?.datos_generales || {};
    const plIn = parsed?.plazos || {};
    const ctIn = parsed?.condiciones_trabajo || {};
    const prestIn = parsed?.prestaciones || {};
    const accionIn = parsed?.accion_intentada || "";

    const cambios = [];
    const ov = { datos_generales: {}, etapas_procesales: {}, acciones_prestaciones: {}, condiciones_trabajo: {}, hechos: {} };

    if (dgIn.numero_expediente) {
      ov.datos_generales.numero_expediente = String(dgIn.numero_expediente).trim();
      cambios.push({ label: "Número de expediente", valor: ov.datos_generales.numero_expediente });
    }
    if (dgIn.numero_unico_identificacion) {
      ov.datos_generales.num_unico = String(dgIn.numero_unico_identificacion).trim();
      cambios.push({ label: "Número único", valor: ov.datos_generales.num_unico });
    }
    if (dgIn.parte_actora) {
      ov.datos_generales.nombre_parte_actora = String(dgIn.parte_actora).trim();
      cambios.push({ label: "Trabajador / parte actora", valor: ov.datos_generales.nombre_parte_actora });
    }

    let matchedEstado = null, matchedCiudad = null, matchedAutoridad = null;
    if (dgIn.estado) {
      matchedEstado = fuzzyMatchOptionsCR(dgIn.estado, estadosOptions || []);
      if (matchedEstado) {
        ov.datos_generales.estado = Number(matchedEstado.value);
        cambios.push({ label: "Estado", valor: matchedEstado.label });
      }
    }
    if (dgIn.ciudad) {
      const ciudadesAll = Object.values(ciudadesByIdCatalogo || {}).map((c) => ({ label: c?.nombre || "", value: c?.id, id_estado: c?.id_estado }));
      const pool = matchedEstado ? ciudadesAll.filter((c) => Number(c.id_estado) === Number(matchedEstado.value)) : ciudadesAll;
      matchedCiudad = fuzzyMatchOptionsCR(dgIn.ciudad, pool.length ? pool : ciudadesAll);
      if (matchedCiudad) {
        ov.datos_generales.ciudad = Number(matchedCiudad.value);
        cambios.push({ label: "Ciudad", valor: matchedCiudad.label });
      }
    }
    if (dgIn.autoridad) {
      const autoridadesOpts = (autoridadesItems || []).map((au) => ({ label: au?.nombre || "", value: au?.id, id_ciudad: au?.id_ciudad }));
      const pool = matchedCiudad ? autoridadesOpts.filter((au) => Number(au.id_ciudad) === Number(matchedCiudad.value)) : autoridadesOpts;
      matchedAutoridad = fuzzyMatchOptionsCR(dgIn.autoridad, pool.length ? pool : autoridadesOpts);
      if (matchedAutoridad) {
        ov.datos_generales.autoridad = Number(matchedAutoridad.value);
        cambios.push({ label: "Autoridad", valor: matchedAutoridad.label });
      }
    }

    if (plIn.fecha_notificacion) {
      ov.datos_generales.fecha_notificacion_demanda = plIn.fecha_notificacion;
      cambios.push({ label: "Fecha de notificación", valor: fmtFechaCR(plIn.fecha_notificacion) });
    }
    if (plIn.fecha_presentacion_demanda) {
      ov.datos_generales.fecha_presentacion_demanda = plIn.fecha_presentacion_demanda;
      cambios.push({ label: "Fecha de presentación de demanda", valor: fmtFechaCR(plIn.fecha_presentacion_demanda) });
    }
    if (plIn.fecha_radicacion) {
      ov.datos_generales.fecha_radicacion_demanda = plIn.fecha_radicacion;
      cambios.push({ label: "Fecha de radicación", valor: fmtFechaCR(plIn.fecha_radicacion) });
    }
    if (plIn.fecha_emision_citatorio_ccl) {
      ov.datos_generales.fecha_emision_citatorio = plIn.fecha_emision_citatorio_ccl;
      cambios.push({ label: "Fecha de emisión de citatorio", valor: fmtFechaCR(plIn.fecha_emision_citatorio_ccl) });
    }
    if (plIn.fecha_constancia_no_conciliacion) {
      ov.datos_generales.fecha_constancia_conciliacion = plIn.fecha_constancia_no_conciliacion;
      cambios.push({ label: "Fecha de constancia de no conciliación", valor: fmtFechaCR(plIn.fecha_constancia_no_conciliacion) });
    }
    if (plIn.fecha_audiencia_conciliatoria) {
      ov.etapas_procesales.fecha_audiencia_conciliatoria = plIn.fecha_audiencia_conciliatoria;
      cambios.push({ label: "Fecha de audiencia conciliatoria", valor: fmtFechaCR(plIn.fecha_audiencia_conciliatoria) });
    }
    if (plIn.observaciones_etapa_actual) {
      ov.etapas_procesales.observaciones_etapa_actual = plIn.observaciones_etapa_actual;
      cambios.push({ label: "Observaciones de la etapa actual", valor: plIn.observaciones_etapa_actual });
    }

    if (accionIn) {
      ov.acciones_prestaciones.accion_intentada = accionIn;
      cambios.push({ label: "Acción intentada", valor: accionIn });
    }
    const prestOut = {};
    let countPrest = 0;
    Object.keys(prestIn).forEach((k) => {
      if (k === "otras_prestaciones") return;
      if (prestIn[k] === true) { prestOut[k] = true; countPrest++; }
    });
    if (countPrest > 0) {
      ov.acciones_prestaciones.prestaciones = { ...(ap?.prestaciones || {}), ...prestOut };
      cambios.push({ label: "Prestaciones reclamadas", valor: `${countPrest} marcada(s)` });
    }
    if (prestIn.otras_prestaciones) {
      ov.acciones_prestaciones.otras_prestaciones = prestIn.otras_prestaciones;
      cambios.push({ label: "Otras prestaciones", valor: prestIn.otras_prestaciones });
    }

    if (ctIn.fecha_ingreso) {
      ov.condiciones_trabajo.fecha_ingreso = ctIn.fecha_ingreso;
      cambios.push({ label: "Fecha de ingreso", valor: fmtFechaCR(ctIn.fecha_ingreso) });
    }
    if (ctIn.ultimo_dia_laborado) {
      ov.condiciones_trabajo.fecha_ultimo_dia_laborado = ctIn.ultimo_dia_laborado;
      cambios.push({ label: "Último día laborado", valor: fmtFechaCR(ctIn.ultimo_dia_laborado) });
    }
    if (ctIn.puesto_o_categoria) {
      ov.condiciones_trabajo.puesto = ctIn.puesto_o_categoria;
      cambios.push({ label: "Puesto / categoría", valor: ctIn.puesto_o_categoria });
    }
    if (ctIn.salario_diario != null && ctIn.salario_diario !== "") {
      ov.condiciones_trabajo.salario_diario = Number(ctIn.salario_diario);
      cambios.push({ label: "Salario diario", valor: ov.condiciones_trabajo.salario_diario });
    }
    if (ctIn.salario_diario_integrado != null && ctIn.salario_diario_integrado !== "") {
      ov.condiciones_trabajo.salario_diario_integrado = Number(ctIn.salario_diario_integrado);
      cambios.push({ label: "Salario diario integrado", valor: ov.condiciones_trabajo.salario_diario_integrado });
    }
    if (ctIn.jornada) {
      ov.condiciones_trabajo.jornada = ctIn.jornada;
      cambios.push({ label: "Jornada", valor: ctIn.jornada });
    }
    if (ctIn.dias_descanso) {
      ov.condiciones_trabajo.dia_descanso = ctIn.dias_descanso;
      cambios.push({ label: "Día(s) de descanso", valor: ctIn.dias_descanso });
    }
    if (ctIn.lugar_prestacion_servicio) {
      ov.condiciones_trabajo.lugar_servicio = ctIn.lugar_prestacion_servicio;
      cambios.push({ label: "Lugar de prestación del servicio", valor: ctIn.lugar_prestacion_servicio });
    }
    if (ctIn.responsable_del_despido) {
      ov.hechos.responsable_despido = ctIn.responsable_del_despido;
      cambios.push({ label: "Responsable del despido", valor: ctIn.responsable_del_despido });
    }
    if (ctIn.jefes_inmediatos) {
      ov.hechos.jefes_inmediatos = ctIn.jefes_inmediatos;
      cambios.push({ label: "Jefes inmediatos", valor: ctIn.jefes_inmediatos });
    }
    if (ctIn.circunstancias_modo_tiempo_lugar) {
      ov.hechos.narrativa = ctIn.circunstancias_modo_tiempo_lugar;
      cambios.push({ label: "Narrativa de hechos", valor: ctIn.circunstancias_modo_tiempo_lugar });
    }

    // Pruebas ofrecidas por la parte actora — mismo catálogo de tipos que usa
    // "Contestación de demanda" en el tab Expediente, para que la IA pueda
    // devolverlas con los mismos nombres de campo.
    const pruebasActoraIn = Array.isArray(parsed?.pruebas_actora) ? parsed.pruebas_actora : [];
    if (pruebasActoraIn.length > 0) {
      const asListaCR = (v) => Array.isArray(v) ? v.filter(Boolean) : String(v || "").split(",").map((s) => s.trim()).filter(Boolean);
      const PRUEBA_ACTORA_LABEL = {
        confesional: "Confesional",
        testimonial: "Testimonial",
        documental_publica: "Documental pública",
        documental_privada: "Documental privada",
        informe_autoridad: "Informe de autoridad",
        pericial: "Pericial",
        inspeccion_ocular: "Inspección ocular",
        medios_electronicos: "Medios electrónicos",
      };
      const overridesActora = {};
      const testigos = [], docsPublicos = [], docsPrivados = [], docsInforme = [], periciales = [], inspecciones = [];
      let mediosTipos = [], mediosDescripcion = "";

      pruebasActoraIn.forEach((p) => {
        const tipo = String(p?.tipo || "").trim().toLowerCase();
        if (!tipo) return;
        let resumen = "";
        if (tipo === "confesional") {
          overridesActora.confesional = [...(overridesActora.confesional || []), { persona_confesar: p.persona_confesar || "", hechos_confesar: p.hechos_confesar || "" }];
          resumen = p.persona_confesar || "";
        } else if (tipo === "testimonial") {
          testigos.push({ nombre: p.nombre || "", relacion: p.relacion || "", contacto: p.contacto || "" });
          resumen = p.nombre || "";
        } else if (tipo === "documental_publica") {
          docsPublicos.push({ nombre: p.nombre || "", descripcion: p.descripcion || "", fecha: p.fecha || "" });
          resumen = p.nombre || "";
        } else if (tipo === "documental_privada") {
          docsPrivados.push({ nombre: p.nombre || "", descripcion: p.descripcion || "", fecha: p.fecha || "" });
          resumen = p.nombre || "";
        } else if (tipo === "informe_autoridad") {
          docsInforme.push({ nombre: p.nombre || "", descripcion: p.descripcion || "", fecha: p.fecha || "" });
          resumen = p.nombre || "";
        } else if (tipo === "pericial") {
          periciales.push({
            tipo_pericial: asListaCR(p.tipo_pericial),
            nombre_perito: p.nombre_perito || "",
            objeto_prueba: p.objeto_prueba || "",
          });
          resumen = [asListaCR(p.tipo_pericial).join(", "), p.nombre_perito].filter(Boolean).join(" — ");
        } else if (tipo === "inspeccion_ocular") {
          inspecciones.push({
            tipo_inspeccion_ocular: asListaCR(p.tipo_inspeccion_ocular),
            nombre_inspeccion_ocular: p.nombre || "",
            objeto_prueba: p.objeto_prueba || "",
          });
          resumen = asListaCR(p.tipo_inspeccion_ocular).join(", ");
        } else if (tipo === "medios_electronicos") {
          mediosTipos = asListaCR(p.tipos);
          mediosDescripcion = p.descripcion || "";
          resumen = mediosTipos.join(", ");
        } else {
          return;
        }
        cambios.push({ label: `Prueba actora — ${PRUEBA_ACTORA_LABEL[tipo] || tipo}`, valor: resumen || "Registrada" });
      });

      if (testigos.length) overridesActora.testimonial = { tipo: safeArr(testimonialActora?.tipo), testigos };
      if (docsPublicos.length) overridesActora.documental_publica = { documentos: docsPublicos };
      if (docsPrivados.length) overridesActora.documental_privada = { documentos: docsPrivados };
      if (docsInforme.length) overridesActora.informe_autoridad = { documentos: docsInforme };
      if (periciales.length) overridesActora.pericial = periciales;
      if (inspecciones.length) overridesActora.inspeccion_ocular = inspecciones;
      if (mediosTipos.length || mediosDescripcion) overridesActora.medios_electronicos = { tipos: mediosTipos, descripcion: mediosDescripcion };

      ov.pruebasActora = overridesActora;
      cambios.push({ label: "Pruebas de la parte actora", valor: `${pruebasActoraIn.length} registrada(s)` });
    }

    if (cambios.length === 0) {
      setCargaRapidaError("No se reconoció ningún dato útil en ese JSON.");
      return;
    }

    const payload = buildTribunalBasePayload(ov);
    setCargaRapidaReview({ payload, cambios });
  };

  const confirmarCargaRapida = async () => {
    if (!cargaRapidaReview) return;
    setCargaRapidaSaving(true);
    try {
      await dispatch(actionTribunalUpdateById(detalle.id, cargaRapidaReview.payload));
      await fetchDetalle();
      notify.success({
        message: "Expediente actualizado",
        description: "Los datos de la carga rápida se guardaron correctamente.",
        placement: "bottomRight",
      });
      cerrarCargaRapida();
    } catch (e) {
      notify.error({
        message: "No se pudo guardar",
        description: e?.response?.data?.detail || e?.message || "No se pudieron guardar los cambios.",
        placement: "bottomRight",
      });
    } finally {
      setCargaRapidaSaving(false);
    }
  };

  const startEditDatosCard = () => {
    setSelectedEstadoId(dg?.estado ? Number(dg.estado) : null);
    setSelectedCiudadId(dg?.ciudad ? Number(dg.ciudad) : null);
    setSelectedAutoridadId(dg?.autoridad ? Number(dg.autoridad) : null);
    setEditingDatosForm({
      num_unico: dg?.num_unico || "",
      numero_expediente: dg?.numero_expediente || "",
      fecha_notificacion_demanda: fechaNotificacionDemanda ? dayjs(fechaNotificacionDemanda) : null,
      fecha_ultimo_dia_laborado: fechaUltimoDiaLaborado ? dayjs(fechaUltimoDiaLaborado) : null,
      fecha_limite_contestacion: fechaLimiteContestacionActual ? dayjs(fechaLimiteContestacionActual) : null,
    });
    setEditingDatosCard(true);
  };

  const handleSaveDatosCard = async () => {
    if (!selectedEstadoId || !selectedCiudadId || !selectedAutoridadId) {
      notify.error({
        message: "Faltan selecciones",
        description: "Selecciona Estado, Ciudad y Autoridad.",
        placement: "bottomRight",
      });
      return;
    }
    if (!editingDatosForm.fecha_notificacion_demanda) {
      notify.error({
        message: "Falta la fecha de notificación",
        description: "Selecciona la fecha de notificación de la demanda.",
        placement: "bottomRight",
      });
      return;
    }
    if (!editingDatosForm.fecha_limite_contestacion) {
      notify.error({
        message: "Falta la fecha límite",
        description: "Selecciona la fecha límite de contestación.",
        placement: "bottomRight",
      });
      return;
    }

    setSavingDatosCard(true);
    try {
      const payload = buildTribunalBasePayload({
        datos_generales: {
          num_unico: (editingDatosForm.num_unico || "").trim() || null,
          numero_expediente: (editingDatosForm.numero_expediente || "").trim() || null,
          estado: Number(selectedEstadoId),
          ciudad: Number(selectedCiudadId),
          autoridad: Number(selectedAutoridadId),
          fecha_notificacion_demanda: editingDatosForm.fecha_notificacion_demanda.format("YYYY-MM-DD"),
        },
        etapas_procesales: {
          fecha_limite_contestacion: editingDatosForm.fecha_limite_contestacion.format("YYYY-MM-DD"),
        },
        condiciones_trabajo: {
          fecha_ultimo_dia_laborado: editingDatosForm.fecha_ultimo_dia_laborado
            ? editingDatosForm.fecha_ultimo_dia_laborado.format("YYYY-MM-DD")
            : null,
        },
      });

      await dispatch(actionTribunalUpdateById(detalle.id, payload));
      await fetchDetalle();
      setEditingDatosCard(false);
      notify.success({
        message: "Expediente actualizado",
        description: "Los cambios se guardaron correctamente.",
        placement: "bottomRight",
      });
    } catch (e) {
      notify.error({
        message: "No se pudo guardar",
        description: e?.response?.data?.detail || e?.message || "No se pudieron guardar los cambios.",
        placement: "bottomRight",
      });
    } finally {
      setSavingDatosCard(false);
    }
  };

  const startEditAccionesCard = () => {
    setEditingAccionesForm({
      trabajador_nombre: ap?.trabajador_nombre || "",
      fecha_presentacion_demanda: fechaPresentacionDemanda ? dayjs(fechaPresentacionDemanda) : null,
      accion_intentada: ap?.accion_intentada || "",
      prestaciones: { ...(ap?.prestaciones || {}) },
    });
    setEditingAccionesCard(true);
  };

  const handleSaveAccionesCard = async () => {
    const trabajadorNombre = (editingAccionesForm.trabajador_nombre || "").trim();
    if (!trabajadorNombre) {
      notify.error({
        message: "Falta el trabajador",
        description: "Escribe el nombre de la parte actora.",
        placement: "bottomRight",
      });
      return;
    }
    if (!editingAccionesForm.accion_intentada) {
      notify.error({
        message: "Falta la acción intentada",
        description: "Selecciona la acción intentada.",
        placement: "bottomRight",
      });
      return;
    }

    setSavingAccionesCard(true);
    try {
      const payload = buildTribunalBasePayload({
        datos_generales: {
          nombre_parte_actora: trabajadorNombre,
          fecha_presentacion_demanda: editingAccionesForm.fecha_presentacion_demanda
            ? editingAccionesForm.fecha_presentacion_demanda.format("YYYY-MM-DD")
            : null,
        },
        acciones_prestaciones: {
          accion_intentada: editingAccionesForm.accion_intentada,
          prestaciones: { ...(editingAccionesForm.prestaciones || {}) },
        },
      });

      await dispatch(actionTribunalUpdateById(detalle.id, payload));
      await fetchDetalle();
      setEditingAccionesCard(false);
      notify.success({
        message: "Expediente actualizado",
        description: "Los cambios se guardaron correctamente.",
        placement: "bottomRight",
      });
    } catch (e) {
      notify.error({
        message: "No se pudo guardar",
        description: e?.response?.data?.detail || e?.message || "No se pudieron guardar los cambios.",
        placement: "bottomRight",
      });
    } finally {
      setSavingAccionesCard(false);
    }
  };

  const startEditHechosCard = () => {
    setEditingHechosForm({
      responsable_despido: hechos?.responsable_despido || "",
      jefes_inmediatos: hechos?.jefes_inmediatos || "",
      narrativa: hechos?.narrativa || "",
    });
    setEditingHechosCard(true);
  };

  const handleSaveHechosCard = async () => {
    setSavingHechosCard(true);
    try {
      const payload = buildTribunalBasePayload({
        hechos: {
          responsable_despido: (editingHechosForm.responsable_despido || "").trim(),
          jefes_inmediatos: (editingHechosForm.jefes_inmediatos || "").trim(),
          narrativa: (editingHechosForm.narrativa || "").trim(),
        },
      });

      await dispatch(actionTribunalUpdateById(detalle.id, payload));
      await fetchDetalle();
      setEditingHechosCard(false);
      notify.success({
        message: "Expediente actualizado",
        description: "Los cambios se guardaron correctamente.",
        placement: "bottomRight",
      });
    } catch (e) {
      notify.error({
        message: "No se pudo guardar",
        description: e?.response?.data?.detail || e?.message || "No se pudieron guardar los cambios.",
        placement: "bottomRight",
      });
    } finally {
      setSavingHechosCard(false);
    }
  };

  const startEditCondicionesCard = () => {
    setEditingCondicionesForm({
      fecha_ingreso: ct?.fecha_ingreso ? dayjs(ct.fecha_ingreso) : null,
      puesto: ct?.puesto || "",
      salario_diario: ct?.salario_diario === "" || ct?.salario_diario == null ? null : Number(ct.salario_diario),
      salario_diario_integrado:
        (ct?.salario_diario_integrado ?? ct?.sdi) === "" || (ct?.salario_diario_integrado ?? ct?.sdi) == null
          ? null
          : Number(ct?.salario_diario_integrado ?? ct?.sdi),
      jornada: ct?.jornada || "",
      dia_descanso: Array.isArray(ct?.dia_descanso) ? ct.dia_descanso : [],
      lugar_servicio: ct?.lugar_servicio || "",
    });
    setEditingCondicionesCard(true);
  };

  const handleDiaDescansoChange = (values) => {
    let next = Array.isArray(values) ? values : [];
    if (next.includes("Sin descanso")) {
      next = ["Sin descanso"];
    }
    setEditingCondicionesForm((f) => ({ ...f, dia_descanso: next }));
  };

  const handleSaveCondicionesCard = async () => {
    setSavingCondicionesCard(true);
    try {
      const payload = buildTribunalBasePayload({
        condiciones_trabajo: {
          fecha_ingreso: editingCondicionesForm.fecha_ingreso
            ? editingCondicionesForm.fecha_ingreso.format("YYYY-MM-DD")
            : null,
          puesto: (editingCondicionesForm.puesto || "").trim(),
          salario_diario:
            editingCondicionesForm.salario_diario === null || editingCondicionesForm.salario_diario === undefined
              ? null
              : Number(editingCondicionesForm.salario_diario),
          salario_diario_integrado:
            editingCondicionesForm.salario_diario_integrado === null ||
            editingCondicionesForm.salario_diario_integrado === undefined
              ? null
              : Number(editingCondicionesForm.salario_diario_integrado),
          jornada: (editingCondicionesForm.jornada || "").trim(),
          dia_descanso: editingCondicionesForm.dia_descanso || [],
          lugar_servicio: (editingCondicionesForm.lugar_servicio || "").trim(),
        },
      });

      await dispatch(actionTribunalUpdateById(detalle.id, payload));
      await fetchDetalle();
      setEditingCondicionesCard(false);
      notify.success({
        message: "Expediente actualizado",
        description: "Los cambios se guardaron correctamente.",
        placement: "bottomRight",
      });
    } catch (e) {
      notify.error({
        message: "No se pudo guardar",
        description: e?.response?.data?.detail || e?.message || "No se pudieron guardar los cambios.",
        placement: "bottomRight",
      });
    } finally {
      setSavingCondicionesCard(false);
    }
  };

  const genRowId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

  const savePruebaSection = async ({ pruebasActora, pruebasDemandada }, { onDone, onFail, setSaving }) => {
    setSaving(true);
    try {
      const payload = buildTribunalBasePayload({ pruebasActora, pruebasDemandada });
      await dispatch(actionTribunalUpdateById(detalle.id, payload));
      await fetchDetalle();
      onDone?.();
      notify.success({
        message: "Expediente actualizado",
        description: "Los cambios se guardaron correctamente.",
        placement: "bottomRight",
      });
    } catch (e) {
      notify.error({
        message: "No se pudo guardar",
        description: e?.response?.data?.detail || e?.message || "No se pudieron guardar los cambios.",
        placement: "bottomRight",
      });
      onFail?.();
    } finally {
      setSaving(false);
    }
  };

  // ---- Testimonial ----
  const startEditTestimonialCard = () => {
    setEditingTestimonialForm(
      testigosActora.map((t) => ({
        _id: genRowId(),
        nombre: t?.nombre || "",
        relacion: t?.relacion || "",
        contacto: t?.contacto || "",
      }))
    );
    setEditingTestimonialCard(true);
  };

  const handleSaveTestimonialCard = () =>
    savePruebaSection(
      {
        pruebasActora: {
          testimonial: {
            tipo: safeArr(testimonialActora?.tipo),
            testigos: editingTestimonialForm.map((t) => ({
              nombre: (t.nombre || "").trim(),
              relacion: (t.relacion || "").trim(),
              contacto: (t.contacto || "").trim(),
            })),
          },
        },
      },
      { onDone: () => setEditingTestimonialCard(false), setSaving: setSavingTestimonialCard }
    );

  // ---- Documental Pública ----
  const startEditDocPublicaCard = () => {
    setEditingDocPublicaForm(
      documentosPublicosActora.map((d) => ({
        _id: genRowId(),
        nombre: d?.nombre || "",
        descripcion: d?.descripcion || "",
        fecha: d?.fecha ? dayjs(d.fecha) : null,
      }))
    );
    setEditingDocPublicaCard(true);
  };

  const handleSaveDocPublicaCard = () =>
    savePruebaSection(
      {
        pruebasActora: {
          documental_publica: {
            documentos: editingDocPublicaForm.map((d) => ({
              nombre: (d.nombre || "").trim(),
              descripcion: (d.descripcion || "").trim(),
              fecha: d.fecha ? d.fecha.format("YYYY-MM-DD") : null,
            })),
          },
        },
      },
      { onDone: () => setEditingDocPublicaCard(false), setSaving: setSavingDocPublicaCard }
    );

  // ---- Documental Privada ----
  const startEditDocPrivadaCard = () => {
    setEditingDocPrivadaForm(
      documentosPrivadosActora.map((d) => ({
        _id: genRowId(),
        nombre: d?.nombre || "",
        descripcion: d?.descripcion || "",
        fecha: d?.fecha ? dayjs(d.fecha) : null,
      }))
    );
    setEditingDocPrivadaCard(true);
  };

  const handleSaveDocPrivadaCard = () =>
    savePruebaSection(
      {
        pruebasActora: {
          documental_privada: {
            documentos: editingDocPrivadaForm.map((d) => ({
              nombre: (d.nombre || "").trim(),
              descripcion: (d.descripcion || "").trim(),
              fecha: d.fecha ? d.fecha.format("YYYY-MM-DD") : null,
            })),
          },
        },
      },
      { onDone: () => setEditingDocPrivadaCard(false), setSaving: setSavingDocPrivadaCard }
    );

  // ---- Pericial ----
  const startEditPericialCard = () => {
    setEditingPericialForm(
      pericialItemsActora.map((item) => ({
        _id: genRowId(),
        tipo: splitJoinedTipo(item?.tipo),
        perito: item?.perito || "",
        objeto: item?.objeto || "",
      }))
    );
    setEditingPericialCard(true);
  };

  const handleSavePericialCard = () =>
    savePruebaSection(
      {
        pruebasActora: {
          pericial: editingPericialForm.map((item) => ({
            tipo_pericial: item.tipo || [],
            nombre_perito: (item.perito || "").trim(),
            objeto_prueba: (item.objeto || "").trim(),
          })),
        },
      },
      { onDone: () => setEditingPericialCard(false), setSaving: setSavingPericialCard }
    );

  // ---- Inspección Ocular ----
  const startEditInspeccionCard = () => {
    setEditingInspeccionForm(
      inspeccionItemsActora.map((item) => ({
        _id: genRowId(),
        tipo: splitJoinedTipo(item?.tipo),
        nombre: item?.nombre || "",
        objeto: item?.objeto || "",
      }))
    );
    setEditingInspeccionCard(true);
  };

  const handleSaveInspeccionCard = () =>
    savePruebaSection(
      {
        pruebasActora: {
          inspeccion_ocular: editingInspeccionForm.map((item) => ({
            tipo_inspeccion_ocular: item.tipo || [],
            nombre_inspeccion_ocular: (item.nombre || "").trim(),
            objeto_prueba: (item.objeto || "").trim(),
          })),
        },
      },
      { onDone: () => setEditingInspeccionCard(false), setSaving: setSavingInspeccionCard }
    );

  // ---- Confesional ----
  const startEditConfesionalCard = () => {
    setEditingConfesionalForm(
      confesionalesActora.map((item) => ({
        _id: genRowId(),
        persona: item?.persona_confesar || "",
        hechos: item?.hechos_confesar || "",
      }))
    );
    setEditingConfesionalCard(true);
  };

  const handleSaveConfesionalCard = () =>
    savePruebaSection(
      {
        pruebasActora: {
          confesional: editingConfesionalForm.map((item) => ({
            persona_confesar: (item.persona || "").trim(),
            hechos_confesar: (item.hechos || "").trim(),
          })),
        },
      },
      { onDone: () => setEditingConfesionalCard(false), setSaving: setSavingConfesionalCard }
    );

  // ---- Confesional de Hechos Propios ----
  const startEditConfesionalPropiosCard = () => {
    setEditingConfesionalPropiosForm(
      confesionalesPropiosActora.map((item) => ({
        _id: genRowId(),
        tipo_prueba: item?.tipo_prueba || "",
        hechos_propios: item?.hechos_propios || "",
      }))
    );
    setEditingConfesionalPropiosCard(true);
  };

  const handleSaveConfesionalPropiosCard = () =>
    savePruebaSection(
      {
        pruebasActora: {
          confesional_hechos_propios: editingConfesionalPropiosForm.map((item) => ({
            tipo_prueba: (item.tipo_prueba || "").trim(),
            hechos_propios: (item.hechos_propios || "").trim(),
          })),
        },
      },
      { onDone: () => setEditingConfesionalPropiosCard(false), setSaving: setSavingConfesionalPropiosCard }
    );

  // ---- Confesional Demandada ----
  const startEditConfesionalDemandadaCard = () => {
    setEditingConfesionalDemandadaForm(
      confesionalesDemandada.map((item) => ({
        _id: genRowId(),
        id_razon_social: item?.id_razon_social ?? null,
      }))
    );
    setEditingConfesionalDemandadaCard(true);
  };

  const handleSaveConfesionalDemandadaCard = () =>
    savePruebaSection(
      {
        pruebasDemandada: {
          confesional: editingConfesionalDemandadaForm
            .filter((item) => item.id_razon_social != null)
            .map((item) => ({ id_razon_social: item.id_razon_social })),
        },
      },
      {
        onDone: () => setEditingConfesionalDemandadaCard(false),
        setSaving: setSavingConfesionalDemandadaCard,
      }
    );

  // ---- Informe de Autoridad ----
  const startEditInformeAutoridadCard = () => {
    setEditingInformeAutoridadForm(
      informesAutoridadActora.map((d) => ({
        _id: genRowId(),
        nombre: d?.nombre || "",
        descripcion: d?.descripcion || "",
        fecha: d?.fecha ? dayjs(d.fecha) : null,
      }))
    );
    setEditingInformeAutoridadCard(true);
  };

  const handleSaveInformeAutoridadCard = () =>
    savePruebaSection(
      {
        pruebasActora: {
          informe_autoridad: {
            documentos: editingInformeAutoridadForm.map((d) => ({
              nombre: (d.nombre || "").trim(),
              descripcion: (d.descripcion || "").trim(),
              fecha: d.fecha ? d.fecha.format("YYYY-MM-DD") : null,
            })),
          },
        },
      },
      { onDone: () => setEditingInformeAutoridadCard(false), setSaving: setSavingInformeAutoridadCard }
    );

  // ---- Medios Electrónicos / Ciencia ----
  const startEditMediosCard = () => {
    setEditingMediosForm({
      tipos: safeArr(mediosElectronicosActora?.tipos),
      descripcion: mediosElectronicosActora?.descripcion || "",
    });
    setEditingMediosCard(true);
  };

  const handleSaveMediosCard = () =>
    savePruebaSection(
      {
        pruebasActora: {
          medios_electronicos: {
            tipos: safeArr(editingMediosForm.tipos),
            descripcion: (editingMediosForm.descripcion || "").trim(),
          },
        },
      },
      { onDone: () => setEditingMediosCard(false), setSaving: setSavingMediosCard }
    );

  // ---- Switches de estatus ----
  const handleToggleChecklist = async (key) => {
    setSavingChecklistKey(key);
    try {
      const payload = buildTribunalBasePayload({
        pruebasChecklist: { [key]: !checklistActora?.[key] },
      });
      await dispatch(actionTribunalUpdateById(detalle.id, payload));
      await fetchDetalle();
      notify.success({
        message: "Actualizado",
        description: "El estatus se guardó correctamente.",
        placement: "bottomRight",
      });
    } catch (e) {
      notify.error({
        message: "No se pudo actualizar",
        description: e?.response?.data?.detail || e?.message || "No se pudo guardar el cambio.",
        placement: "bottomRight",
      });
    } finally {
      setSavingChecklistKey(null);
    }
  };

  return (
    <div className="lxp-root">
      <div className="lxp-wrap">
        <div className="">
          {/* LEFT */}
          <div>
            {/* summary — mismo componente y clases que el header de Conciliación.
                El wrapper laboral-exp-main trae la regla de color del botón
                Eliminar (.ant-btn-primary.ant-btn-dangerous); se neutraliza su
                fondo/padding de página para no pisar el layout de lxp-root. */}
            <div className="laboral-exp-main" style={{ background: "transparent", padding: 0, minHeight: "auto" }}>
            <ConfigProvider wave={{ showEffect: showInsetEffect }}>
            <Card className="laboral-exp-summary-card p-2">
              <div className="laboral-exp-summary-title">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "nowrap", gap: 16 }}>
                  <Title
                    level={4}
                    style={{
                      margin: 0,
                      fontSize: 28,
                      fontWeight: 650,
                      letterSpacing: "-0.01em",
                      lineHeight: 1.2,
                      color: "#0f172a",
                      flex: "1 1 auto",
                      minWidth: 0,
                    }}
                  >
                    {(ap?.trabajador_nombre || "—").toUpperCase()} VS{" "}
                    {demandadaTitle.toUpperCase()}
                  </Title>
                  <DataBadge
                    color={STATUS_PILL_HEX[estatusPill.kind] || estatusPill.kind}
                    className="d-inline-flex align-items-center gap-1 status-pill-badge"
                    style={{ flex: "0 0 auto", marginLeft: "auto" }}
                  >
                    {estatusPill.label}
                  </DataBadge>
                </div>
                <Text type="secondary" className="laboral-exp-subtitle">
                  Núm. de expediente: {dg?.numero_expediente || "Sin número"}
                </Text>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16, marginTop: 8 }}>
                <Space size="large" wrap className="laboral-exp-summary-meta">
                  <Space className="laboral-exp-meta-item">
                    <i className="fa-solid fa-location-dot" />
                    <Text>
                      Creado: {ciudadName}
                      {estadoName !== "—" ? `, ${estadoName}` : ""}
                    </Text>
                  </Space>
                  <Space className="laboral-exp-meta-item">
                    <i className="fa-solid fa-calendar-days" />
                    <Text>Creado: {fmtDateLong(detalle?.datetime)}</Text>
                  </Space>
                  <Space className="laboral-exp-meta-item">
                    <i className="fa-solid fa-address-card" />
                    <Text>Creado por: {toTitleCase(detalle?.nombre_usuario)}</Text>
                  </Space>
                </Space>

                <div style={{ flex: "0 0 auto", marginLeft: "auto" }}>
                  <Space>
                    <Button
                      onClick={() => setCargaRapidaOpen(true)}
                      className="laboral-exp-btn-clean"
                      icon={<span className="material-symbols-outlined" style={{ fontSize: 15, verticalAlign: "middle" }}>bolt</span>}
                    >
                      Carga rápida
                    </Button>
                    {canEliminar && (
                    <Button
                      danger
                      type="primary"
                      icon={<i className="fa-solid fa-trash" />}
                      style={{ borderRadius: 8 }}
                      onClick={handleDeleteExpediente}
                      loading={deletingExp}
                      disabled={deletingExp}
                    >
                      Eliminar
                    </Button>
                    )}
                  </Space>
                </div>
              </div>

              <div className="laboral-exp-header-tabs">
                {[
                  { key: "1", label: "Principal", icon: <i className="fa-solid fa-file" /> },
                  { key: "2", label: "Expediente", icon: <i className="fa-solid fa-folder-open" /> },
                  { key: "4", label: "Cuantificaciones", icon: <i className="fa-solid fa-calculator" /> },
                  { key: "6", label: "Terminación", icon: <i className="fa-solid fa-scale-balanced" /> },
                  { key: "7", label: "Actividad", icon: <i className="fa-solid fa-list" /> },
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    className={`laboral-exp-tab-btn ${activeMainTab === t.key ? "laboral-exp-tab-btn-active" : ""}`}
                    onClick={() => setActiveMainTab(t.key)}
                  >
                    {t.icon}
                    {t.label}
                  </button>
                ))}
              </div>
            </Card>
            </ConfigProvider>
            </div>

            {/* TAB CONTENT */}
            {activeMainTab === "1" && (
              <>
                {/* Datos expediente — mismas clases laboral-exp-* que usa la
                    tarjeta "Datos del expediente" de Conciliación, para que
                    icono, tamaños de texto y espaciados sean idénticos. */}
                <Row gutter={[16, 16]} align="stretch" style={{ marginTop: 18 }}>
                <Col xs={24} lg={20}>
                <Card className="laboral-exp-detail-card laboral-exp-detail-card-main p-2" style={{ height: "100%" }}>
                  <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div className="laboral-exp-icon-badge">
                        <i className="fa-solid fa-file" />
                      </div>
                      <h3 className="laboral-exp-card-header-title">Datos del Expediente</h3>
                    </div>

                    {!editingDatosCard && (
                      <Button onClick={startEditDatosCard} className="laboral-exp-btn-clean">
                        Editar
                      </Button>
                    )}
                  </div>

                  {editingDatosCard ? (
                    <div className="laboral-exp-edit-form">
                      <div className="laboral-exp-edit-grid">
                        <div className="laboral-exp-edit-field">
                          <span className="laboral-exp-edit-label">Estado</span>
                          <Select
                            style={{ width: "100%" }}
                            showSearch
                            optionFilterProp="label"
                            placeholder="Selecciona un estado"
                            options={estadosOptionsFinal}
                            value={selectedEstadoId}
                            onChange={(v) => {
                              setSelectedEstadoId(v ?? null);
                              setSelectedCiudadId(null);
                              setSelectedAutoridadId(null);
                            }}
                          />
                        </div>

                        <div className="laboral-exp-edit-field">
                          <span className="laboral-exp-edit-label">Ciudad</span>
                          <Select
                            style={{ width: "100%" }}
                            showSearch
                            optionFilterProp="label"
                            disabled={!selectedEstadoId}
                            placeholder={!selectedEstadoId ? "Selecciona un estado primero" : "Selecciona una ciudad"}
                            options={ciudadesOptionsFinal}
                            value={selectedCiudadId}
                            onChange={(v) => {
                              setSelectedCiudadId(v ?? null);
                              setSelectedAutoridadId(null);
                            }}
                          />
                        </div>

                        <div className="laboral-exp-edit-field">
                          <span className="laboral-exp-edit-label">Autoridad</span>
                          <Select
                            style={{ width: "100%" }}
                            showSearch
                            optionFilterProp="label"
                            disabled={!selectedCiudadId}
                            placeholder={!selectedCiudadId ? "Selecciona una ciudad primero" : "Selecciona una autoridad"}
                            options={autoridadOptions}
                            value={selectedAutoridadId}
                            onChange={(v) => setSelectedAutoridadId(v ?? null)}
                          />
                        </div>

                        <div className="laboral-exp-edit-field">
                          <span className="laboral-exp-edit-label">Número único</span>
                          <Input
                            value={editingDatosForm.num_unico}
                            onChange={(e) => setEditingDatosForm((f) => ({ ...f, num_unico: e.target.value }))}
                          />
                        </div>

                        <div className="laboral-exp-edit-field">
                          <span className="laboral-exp-edit-label">Número de expediente</span>
                          <Input
                            value={editingDatosForm.numero_expediente}
                            onChange={(e) => setEditingDatosForm((f) => ({ ...f, numero_expediente: e.target.value }))}
                          />
                        </div>

                        <div className="laboral-exp-edit-field">
                          <span className="laboral-exp-edit-label">Fecha notificación demanda</span>
                          <ScrollDatePicker
                            style={{ width: "100%" }}
                            value={editingDatosForm.fecha_notificacion_demanda}
                            onChange={(v) => setEditingDatosForm((f) => ({ ...f, fecha_notificacion_demanda: v }))}
                            format="DD/MM/YYYY"
                          />
                        </div>

                        <div className="laboral-exp-edit-field">
                          <span className="laboral-exp-edit-label">Último día laborado</span>
                          <ScrollDatePicker
                            style={{ width: "100%" }}
                            value={editingDatosForm.fecha_ultimo_dia_laborado}
                            onChange={(v) => setEditingDatosForm((f) => ({ ...f, fecha_ultimo_dia_laborado: v }))}
                            format="DD/MM/YYYY"
                          />
                        </div>

                        <div className="laboral-exp-edit-field">
                          <span className="laboral-exp-edit-label">Límite contestación</span>
                          <ScrollDatePicker
                            style={{ width: "100%" }}
                            value={editingDatosForm.fecha_limite_contestacion}
                            onChange={(v) => setEditingDatosForm((f) => ({ ...f, fecha_limite_contestacion: v }))}
                            format="DD/MM/YYYY"
                          />
                        </div>
                      </div>

                      <div className="laboral-exp-edit-actions">
                        <Button onClick={() => setEditingDatosCard(false)} className="laboral-exp-btn-clean">
                          Cancelar
                        </Button>
                        <HappyProvider>
                          <Button
                            type="primary"
                            loading={savingDatosCard}
                            onClick={handleSaveDatosCard}
                            className="laboral-exp-btn-save"
                            icon={<i className="fa-solid fa-floppy-disk" />}
                          >
                            Guardar
                          </Button>
                        </HappyProvider>
                      </div>
                    </div>
                  ) : (
                    <div className="laboral-exp-field-grid lxp-field-grid-3">
                      <Field label="Número único" value={dg?.num_unico || "—"} />
                      <Field label="Número de expediente" value={dg?.numero_expediente || "—"} />
                      <Field label="Estado" value={estadoName} />
                      <Field label="Ciudad" value={ciudadName} />
                      <Field label="Fecha notificación demanda" value={fmtDateLong(fechaNotificacionDemanda)} />
                      <Field label="Límite contestación" value={fmtDateLong(fechaLimiteContestacionActual)} />
                      <Field label="Autoridad" value={autoridadName} />
                      <Field label="Último día laborado" value={fmtDateLong(fechaUltimoDiaLaborado)} />
                    </div>
                  )}
                </Card>
                </Col>

                {/* Cuadro azul — próxima audiencia (conciliatoria/preliminar/juicio)
                    y términos críticos vigentes, mismas clases laboral-exp-next-aud-*
                    que usa el cuadro azul de Conciliación. */}
                <Col xs={24} lg={4}>
                  <div className="laboral-exp-next-aud-card" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                    <div className="laboral-exp-next-aud-header">
                      <span className="laboral-exp-chip-label">
                        {proximaAudienciaTribunal ? "Próxima audiencia" : "Audiencias"}
                      </span>
                    </div>
                    <div className="laboral-exp-next-aud-body">
                      {proximaAudienciaTribunal ? (
                        <>
                          <Text className="laboral-exp-next-aud-date">{fmtDateLong(proximaAudienciaTribunal.fecha)}</Text>
                          <Text className="laboral-exp-next-aud-sub" style={{ display: "block", marginTop: 6 }}>
                            {proximaAudienciaTribunal.label}
                          </Text>
                        </>
                      ) : (
                        <Text className="laboral-exp-next-aud-date">Sin audiencia programada</Text>
                      )}
                    </div>

                    {terminosCriticosTribunal.length > 0 && (
                      <div className="laboral-exp-next-aud-footer" style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
                        <Text className="laboral-exp-chip-label" style={{ fontSize: 11 }}>Términos críticos</Text>
                        {terminosCriticosTribunal.map((t, i) => (
                          <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12.5, color: "#fff" }}>
                            <span style={{ color: "rgba(255,255,255,0.85)" }}>{t.label}</span>
                            <b style={{ color: "#fff" }}>{fmtDateLong(t.fecha)}</b>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Col>
                </Row>

                {/* Acciones y prestaciones */}
                <div className="lxp-card" style={{ marginTop: 18 }}>
                  <div className="lxp-section-head" style={{ justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                        <i className="fa-solid fa-list-check" />
                      </div>
                      <h3 className="lxp-section-title">Acciones y prestaciones</h3>
                    </div>

                    {!editingAccionesCard && (
                      <Button onClick={startEditAccionesCard} className="laboral-exp-btn-clean">
                        Editar
                      </Button>
                    )}
                  </div>

                  {editingAccionesCard ? (
                    <div className="laboral-exp-edit-form">
                      <div className="laboral-exp-edit-grid">
                        <div className="laboral-exp-edit-field">
                          <span className="laboral-exp-edit-label">Trabajador</span>
                          <Input
                            value={editingAccionesForm.trabajador_nombre}
                            onChange={(e) =>
                              setEditingAccionesForm((f) => ({ ...f, trabajador_nombre: e.target.value }))
                            }
                          />
                        </div>

                        <div className="laboral-exp-edit-field">
                          <span className="laboral-exp-edit-label">Fecha presentación demanda</span>
                          <ScrollDatePicker
                            style={{ width: "100%" }}
                            value={editingAccionesForm.fecha_presentacion_demanda}
                            onChange={(v) =>
                              setEditingAccionesForm((f) => ({ ...f, fecha_presentacion_demanda: v }))
                            }
                            format="DD/MM/YYYY"
                          />
                        </div>

                        <div className="laboral-exp-edit-field">
                          <span className="laboral-exp-edit-label">Acción intentada</span>
                          <Select
                            style={{ width: "100%" }}
                            placeholder="Selecciona una acción"
                            options={ACCION_INTENTADA_OPTIONS}
                            value={editingAccionesForm.accion_intentada || undefined}
                            onChange={(v) => setEditingAccionesForm((f) => ({ ...f, accion_intentada: v ?? "" }))}
                          />
                        </div>
                      </div>

                      <div className="laboral-exp-edit-field" style={{ marginTop: 20 }}>
                        <span className="laboral-exp-edit-label">Prestaciones reclamadas</span>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                            columnGap: 24,
                            rowGap: 18,
                            marginTop: 12,
                            marginBottom: 8,
                          }}
                        >
                          {PRESTACIONES_OPTIONS.map(([key, label]) => (
                            <Checkbox
                              key={key}
                              checked={!!editingAccionesForm.prestaciones?.[key]}
                              onChange={(e) =>
                                setEditingAccionesForm((f) => ({
                                  ...f,
                                  prestaciones: { ...(f.prestaciones || {}), [key]: e.target.checked },
                                }))
                              }
                              style={{ alignItems: "flex-start" }}
                            >
                              <span style={{ lineHeight: 1.4 }}>{label}</span>
                            </Checkbox>
                          ))}
                        </div>
                      </div>

                      <div className="laboral-exp-edit-actions">
                        <Button onClick={() => setEditingAccionesCard(false)} className="laboral-exp-btn-clean">
                          Cancelar
                        </Button>
                        <HappyProvider>
                          <Button
                            type="primary"
                            loading={savingAccionesCard}
                            onClick={handleSaveAccionesCard}
                            className="laboral-exp-btn-save"
                            icon={<i className="fa-solid fa-floppy-disk" />}
                          >
                            Guardar
                          </Button>
                        </HappyProvider>
                      </div>
                    </div>
                  ) : (
                    <div className="lxp-ap-grid lxp-ap-grid--4">
                      <div className="lxp-ap-row">
                        <div className="laboral-exp-field-label" style={{ marginBottom: 4 }}>Trabajador</div>
                        <div className="laboral-exp-field-value">{ap?.trabajador_nombre || "—"}</div>
                      </div>

                      <div className="lxp-ap-row">
                        <div className="laboral-exp-field-label" style={{ marginBottom: 4 }}>Acción intentada</div>
                        <div className="lxp-ap-pill">{ap?.accion_intentada || "—"}</div>
                      </div>

                      <div className="lxp-ap-row">
                        <div className="laboral-exp-field-label" style={{ marginBottom: 4 }}>Fecha presentación demanda</div>
                        <div className="laboral-exp-field-value">{fmtDateLong(fechaPresentacionDemanda)}</div>
                      </div>

                      <div className="lxp-ap-row">
                        <div className="laboral-exp-field-label" style={{ marginBottom: 4 }}>Prestaciones reclamadas</div>
                        <div className="lxp-chip-row">
                          {prestaciones.length ? (
                            prestaciones.map((k) => (
                              <span key={k} className="lxp-chip">
                                {toTitleCase(k.replaceAll("_", " "))}
                              </span>
                            ))
                          ) : (
                            <span className="lxp-chip" style={{ opacity: 0.6 }}>—</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Bottom 2 cards */}
                <div className="lxp-bottom" style={{ marginTop: 18 }}>
                  {/* Hechos */}
                  <div className="lxp-card">
                    <div className="lxp-subhead" style={{ justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                          <i className="fa-solid fa-book" />
                        </div>
                        <h3 className="lxp-section-title">Hechos y Narrativa</h3>
                      </div>

                      {!editingHechosCard && (
                        <Button onClick={startEditHechosCard} className="laboral-exp-btn-clean">
                          Editar
                        </Button>
                      )}
                    </div>

                    {editingHechosCard ? (
                      <div className="laboral-exp-edit-form">
                        <div className="laboral-exp-edit-grid">
                          <div className="laboral-exp-edit-field">
                            <span className="laboral-exp-edit-label">Responsable despido</span>
                            <Input
                              value={editingHechosForm.responsable_despido}
                              onChange={(e) =>
                                setEditingHechosForm((f) => ({ ...f, responsable_despido: e.target.value }))
                              }
                            />
                          </div>

                          <div className="laboral-exp-edit-field">
                            <span className="laboral-exp-edit-label">Jefes inmediatos</span>
                            <Input
                              value={editingHechosForm.jefes_inmediatos}
                              onChange={(e) =>
                                setEditingHechosForm((f) => ({ ...f, jefes_inmediatos: e.target.value }))
                              }
                            />
                          </div>
                        </div>

                        <div className="laboral-exp-edit-field" style={{ marginTop: 16 }}>
                          <span className="laboral-exp-edit-label">Narrativa principal</span>
                          <Input.TextArea
                            rows={4}
                            value={editingHechosForm.narrativa}
                            onChange={(e) => setEditingHechosForm((f) => ({ ...f, narrativa: e.target.value }))}
                          />
                        </div>

                        <div className="laboral-exp-edit-actions">
                          <Button onClick={() => setEditingHechosCard(false)} className="laboral-exp-btn-clean">
                            Cancelar
                          </Button>
                          <HappyProvider>
                            <Button
                              type="primary"
                              loading={savingHechosCard}
                              onClick={handleSaveHechosCard}
                              className="laboral-exp-btn-save"
                              icon={<i className="fa-solid fa-floppy-disk" />}
                            >
                              Guardar
                            </Button>
                          </HappyProvider>
                        </div>
                      </div>
                    ) : (
                      <div className="lxp-hechos">
                        <div className="lxp-mini-cards">
                          <div className="lxp-mini-card">
                            <div className="laboral-exp-field-label" style={{ marginBottom: 4 }}>Responsable despido</div>
                            <div className="laboral-exp-field-value">{hechos?.responsable_despido || "—"}</div>
                          </div>
                          <div className="lxp-mini-card">
                            <div className="laboral-exp-field-label" style={{ marginBottom: 4 }}>Jefes inmediatos</div>
                            <div className="laboral-exp-field-value">{hechos?.jefes_inmediatos || "—"}</div>
                          </div>
                        </div>

                        <div className="lxp-narr">
                          <div className="laboral-exp-field-label" style={{ marginBottom: 4 }}>Narrativa principal</div>
                          {hechos?.narrativa ? (
                            <div className="lxp-narr-box">
                              <div className="lxp-quote">❞</div>
                              <div className="lxp-narr-text">{`"${hechos.narrativa}"`}</div>
                            </div>
                          ) : (
                            <div className="lxp-narr-box lxp-narr-box--empty">
                              <span className="lxp-narr-empty-text">Sin narrativa registrada</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Condiciones */}
                  <div className="lxp-card">
                    <div className="lxp-subhead" style={{ justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                          <i className="fa-solid fa-helmet-safety" />
                        </div>
                        <h3 className="lxp-section-title">Condiciones de Trabajo</h3>
                      </div>

                      {!editingCondicionesCard && (
                        <Button onClick={startEditCondicionesCard} className="laboral-exp-btn-clean">
                          Editar
                        </Button>
                      )}
                    </div>

                    {editingCondicionesCard ? (
                      <div className="laboral-exp-edit-form">
                        <div className="laboral-exp-edit-grid">
                          <div className="laboral-exp-edit-field">
                            <span className="laboral-exp-edit-label">Fecha ingreso</span>
                            <ScrollDatePicker
                              style={{ width: "100%" }}
                              value={editingCondicionesForm.fecha_ingreso}
                              onChange={(v) => setEditingCondicionesForm((f) => ({ ...f, fecha_ingreso: v }))}
                              format="DD/MM/YYYY"
                            />
                          </div>

                          <div className="laboral-exp-edit-field">
                            <span className="laboral-exp-edit-label">Puesto</span>
                            <Input
                              value={editingCondicionesForm.puesto}
                              onChange={(e) => setEditingCondicionesForm((f) => ({ ...f, puesto: e.target.value }))}
                            />
                          </div>

                          <div className="laboral-exp-edit-field">
                            <span className="laboral-exp-edit-label">Salario Diario</span>
                            <InputNumber
                              style={{ width: "100%" }}
                              addonBefore="$"
                              min={0}
                              step={0.01}
                              value={editingCondicionesForm.salario_diario}
                              onChange={(v) => setEditingCondicionesForm((f) => ({ ...f, salario_diario: v }))}
                            />
                          </div>

                          <div className="laboral-exp-edit-field">
                            <span className="laboral-exp-edit-label">SDI</span>
                            <InputNumber
                              style={{ width: "100%" }}
                              addonBefore="$"
                              min={0}
                              step={0.01}
                              value={editingCondicionesForm.salario_diario_integrado}
                              onChange={(v) =>
                                setEditingCondicionesForm((f) => ({ ...f, salario_diario_integrado: v }))
                              }
                            />
                          </div>

                          <div className="laboral-exp-edit-field">
                            <span className="laboral-exp-edit-label">Lugar servicio</span>
                            <Input
                              value={editingCondicionesForm.lugar_servicio}
                              onChange={(e) =>
                                setEditingCondicionesForm((f) => ({ ...f, lugar_servicio: e.target.value }))
                              }
                            />
                          </div>

                          <div className="laboral-exp-edit-field">
                            <span className="laboral-exp-edit-label">Jornada</span>
                            <Input
                              value={editingCondicionesForm.jornada}
                              onChange={(e) => setEditingCondicionesForm((f) => ({ ...f, jornada: e.target.value }))}
                            />
                          </div>

                          <div className="laboral-exp-edit-field">
                            <span className="laboral-exp-edit-label">Día descanso</span>
                            <Select
                              mode="multiple"
                              allowClear
                              style={{ width: "100%" }}
                              placeholder="Seleccionar día(s)"
                              options={DIA_DESCANSO_OPTIONS}
                              value={editingCondicionesForm.dia_descanso || []}
                              onChange={handleDiaDescansoChange}
                            />
                          </div>
                        </div>

                        <div className="laboral-exp-edit-actions">
                          <Button onClick={() => setEditingCondicionesCard(false)} className="laboral-exp-btn-clean">
                            Cancelar
                          </Button>
                          <HappyProvider>
                            <Button
                              type="primary"
                              loading={savingCondicionesCard}
                              onClick={handleSaveCondicionesCard}
                              className="laboral-exp-btn-save"
                              icon={<i className="fa-solid fa-floppy-disk" />}
                            >
                              Guardar
                            </Button>
                          </HappyProvider>
                        </div>
                      </div>
                    ) : (
                      <div className="lxp-ct-grid">
                        <div className="lxp-ct-box">
                          <div className="lxp-ct-box-head">
                            <span className="lxp-ct-box-icon">
                              <span className="material-symbols-outlined">calendar_today</span>
                            </span>
                            <span className="lxp-ct-box-label">Fecha ingreso</span>
                          </div>
                          <div className="lxp-ct-box-value">{fmtShort(ct?.fecha_ingreso)}</div>
                        </div>

                        <div className="lxp-ct-box">
                          <div className="lxp-ct-box-head">
                            <span className="lxp-ct-box-icon">
                              <span className="material-symbols-outlined">event_busy</span>
                            </span>
                            <span className="lxp-ct-box-label">Último día laborado</span>
                          </div>
                          <div className="lxp-ct-box-value">{fmtShort(fechaUltimoDiaLaborado)}</div>
                        </div>

                        <div className="lxp-ct-box">
                          <div className="lxp-ct-box-head">
                            <span className="lxp-ct-box-icon">
                              <span className="material-symbols-outlined">badge</span>
                            </span>
                            <span className="lxp-ct-box-label">Puesto</span>
                          </div>
                          <div className="lxp-ct-box-value">{ct?.puesto || "—"}</div>
                        </div>

                        <div className="lxp-ct-box">
                          <div className="lxp-ct-box-head">
                            <span className="lxp-ct-box-icon">
                              <span className="material-symbols-outlined">payments</span>
                            </span>
                            <span className="lxp-ct-box-label">Salario diario</span>
                          </div>
                          <div className="lxp-ct-box-value">{ct?.salario_diario ? `$${Number(ct.salario_diario).toFixed(2)}` : "—"}</div>
                        </div>

                        <div className="lxp-ct-box">
                          <div className="lxp-ct-box-head">
                            <span className="lxp-ct-box-icon">
                              <span className="material-symbols-outlined">request_quote</span>
                            </span>
                            <span className="lxp-ct-box-label">SDI</span>
                          </div>
                          <div className="lxp-ct-box-value">
                            {ct?.sdi || ct?.salario_diario_integrado
                              ? `$${Number(ct?.sdi || ct?.salario_diario_integrado).toFixed(2)}`
                              : "—"}
                          </div>
                        </div>

                        <div className="lxp-ct-box lxp-ct-box--span2">
                          <div className="lxp-ct-box-head">
                            <span className="lxp-ct-box-icon">
                              <span className="material-symbols-outlined">location_on</span>
                            </span>
                            <span className="lxp-ct-box-label">Lugar servicio</span>
                          </div>
                          <div className="lxp-ct-box-value">{ct?.lugar_servicio || "—"}</div>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Pruebas de la actora — orden del art. 776 LFT: confesional, documental, informe, testimonial, pericial, inspección */}
               


                </div>
                 <div className="lxp-pruebas-wrap">
                  <Card className="laboral-exp-detail-card laboral-exp-detail-card-main p-2" style={{ marginBottom: 18 }}>
                    <div className="laboral-exp-card-header" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: "none" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div className="laboral-exp-icon-badge">
                          <i className="fa-solid fa-folder-open" />
                        </div>
                        <div>
                          <h3 className="laboral-exp-card-header-title">Pruebas de la Actora</h3>
                          <div className="lxp-pruebas-subtitle">
                            Resumen de pruebas y evidencias registradas en el proceso judicial.
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>

                  <div className="lxp-docs-grid-3">
                  <section className="lxp-prueba-card">
                    <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                          <i className="fa-solid fa-magnifying-glass" />
                        </div>
                        <h3 className="lxp-section-title">
                          Confesional Demandada{" "}
                          <span className="lxp-section-count">({confesionalesActora.length})</span>
                        </h3>
                      </div>
                      {!editingConfesionalCard && (
                        <Button onClick={startEditConfesionalCard} className="laboral-exp-btn-clean">
                          Editar
                        </Button>
                      )}
                    </div>

                    {editingConfesionalCard ? (
                      <div className="laboral-exp-edit-form">
                        {editingConfesionalForm.map((item, idx) => (
                          <div key={item._id} className="lxp-edit-row-card">
                            <div className="lxp-edit-row-head">
                              <span>Confesional {idx + 1}</span>
                              <Button
                                size="small"
                                className="laboral-exp-btn-clean"
                                onClick={() =>
                                  setEditingConfesionalForm((rows) => rows.filter((r) => r._id !== item._id))
                                }
                              >
                                Quitar
                              </Button>
                            </div>
                            <div className="laboral-exp-edit-grid">
                              <div className="laboral-exp-edit-field">
                                <span className="laboral-exp-edit-label">Persona a confesar</span>
                                <Input
                                  value={item.persona}
                                  onChange={(e) =>
                                    setEditingConfesionalForm((rows) =>
                                      rows.map((r) => (r._id === item._id ? { ...r, persona: e.target.value } : r))
                                    )
                                  }
                                />
                              </div>
                              <div className="laboral-exp-edit-field">
                                <span className="laboral-exp-edit-label">Hechos a confesar</span>
                                <Input.TextArea
                                  rows={2}
                                  value={item.hechos}
                                  onChange={(e) =>
                                    setEditingConfesionalForm((rows) =>
                                      rows.map((r) => (r._id === item._id ? { ...r, hechos: e.target.value } : r))
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        ))}

                        <Button
                          type="dashed"
                          block
                          icon={<i className="fa-solid fa-plus" />}
                          onClick={() =>
                            setEditingConfesionalForm((rows) => [
                              ...rows,
                              { _id: genRowId(), persona: "", hechos: "" },
                            ])
                          }
                        >
                          Agregar confesional
                        </Button>

                        <div className="laboral-exp-edit-actions">
                          <Button onClick={() => setEditingConfesionalCard(false)} className="laboral-exp-btn-clean">
                            Cancelar
                          </Button>
                          <HappyProvider>
                            <Button
                              type="primary"
                              loading={savingConfesionalCard}
                              onClick={handleSaveConfesionalCard}
                              className="laboral-exp-btn-save"
                              icon={<i className="fa-solid fa-floppy-disk" />}
                            >
                              Guardar
                            </Button>
                          </HappyProvider>
                        </div>
                      </div>
                    ) : confesionalesActora.length > 0 ? (
                      <div className="lxp-prueba-stack">
                        {confesionalesActora.map((item, idx) => (
                          <div key={item?.id || `${item?.persona_confesar}-${idx}`} className="lxp-prueba-block">
                            <div className="lxp-prueba-grid lxp-prueba-grid--2">
                              <div className="lxp-kv">
                                <div className="lxp-k">Persona a confesar</div>
                                <div className="lxp-v">{item?.persona_confesar || "—"}</div>
                              </div>
                              <div className="lxp-kv">
                                <div className="lxp-k">Hechos a confesar</div>
                                <div className="lxp-v lxp-prueba-text">{item?.hechos_confesar || "—"}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="lxp-empty-inline">Sin confesional registrada</div>
                    )}
                  </section>

                  <section className="lxp-prueba-card">
                    <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                          <i className="fa-solid fa-user-check" />
                        </div>
                        <h3 className="lxp-section-title">
                          Confesional Hechos Propios{" "}
                          <span className="lxp-section-count">({confesionalesPropiosActora.length})</span>
                        </h3>
                      </div>
                      {!editingConfesionalPropiosCard && (
                        <Button onClick={startEditConfesionalPropiosCard} className="laboral-exp-btn-clean">
                          Editar
                        </Button>
                      )}
                    </div>

                    {editingConfesionalPropiosCard ? (
                      <div className="laboral-exp-edit-form">
                        {editingConfesionalPropiosForm.map((item, idx) => (
                          <div key={item._id} className="lxp-edit-row-card">
                            <div className="lxp-edit-row-head">
                              <span>Confesional de hechos propios {idx + 1}</span>
                              <Button
                                size="small"
                                className="laboral-exp-btn-clean"
                                onClick={() =>
                                  setEditingConfesionalPropiosForm((rows) => rows.filter((r) => r._id !== item._id))
                                }
                              >
                                Quitar
                              </Button>
                            </div>
                            <div className="laboral-exp-edit-grid">
                              <div className="laboral-exp-edit-field">
                                <span className="laboral-exp-edit-label">Tipo de prueba</span>
                                <Input
                                  value={item.tipo_prueba}
                                  onChange={(e) =>
                                    setEditingConfesionalPropiosForm((rows) =>
                                      rows.map((r) =>
                                        r._id === item._id ? { ...r, tipo_prueba: e.target.value } : r
                                      )
                                    )
                                  }
                                />
                              </div>
                              <div className="laboral-exp-edit-field">
                                <span className="laboral-exp-edit-label">Hechos propios</span>
                                <Input.TextArea
                                  rows={2}
                                  value={item.hechos_propios}
                                  onChange={(e) =>
                                    setEditingConfesionalPropiosForm((rows) =>
                                      rows.map((r) =>
                                        r._id === item._id ? { ...r, hechos_propios: e.target.value } : r
                                      )
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        ))}

                        <Button
                          type="dashed"
                          block
                          icon={<i className="fa-solid fa-plus" />}
                          onClick={() =>
                            setEditingConfesionalPropiosForm((rows) => [
                              ...rows,
                              { _id: genRowId(), tipo_prueba: "", hechos_propios: "" },
                            ])
                          }
                        >
                          Agregar confesional de hechos propios
                        </Button>

                        <div className="laboral-exp-edit-actions">
                          <Button
                            onClick={() => setEditingConfesionalPropiosCard(false)}
                            className="laboral-exp-btn-clean"
                          >
                            Cancelar
                          </Button>
                          <HappyProvider>
                            <Button
                              type="primary"
                              loading={savingConfesionalPropiosCard}
                              onClick={handleSaveConfesionalPropiosCard}
                              className="laboral-exp-btn-save"
                              icon={<i className="fa-solid fa-floppy-disk" />}
                            >
                              Guardar
                            </Button>
                          </HappyProvider>
                        </div>
                      </div>
                    ) : confesionalesPropiosActora.length > 0 ? (
                      <div className="lxp-prueba-stack">
                        {confesionalesPropiosActora.map((item, idx) => (
                          <div key={item?.id || `${item?.tipo_prueba}-${idx}`} className="lxp-prueba-block">
                            <div className="lxp-prueba-grid lxp-prueba-grid--2">
                              <div className="lxp-kv">
                                <div className="lxp-k">Tipo de prueba</div>
                                <div className="lxp-v">{item?.tipo_prueba || "—"}</div>
                              </div>
                              <div className="lxp-kv">
                                <div className="lxp-k">Hechos propios</div>
                                <div className="lxp-v lxp-prueba-text">{item?.hechos_propios || "—"}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="lxp-empty-inline">Sin confesional de hechos propios registrada</div>
                    )}
                  </section>

                  <section className="lxp-prueba-card">
                    <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                          <i className="fa-solid fa-user-check" />
                        </div>
                        <h3 className="lxp-section-title">Confesional Expresa</h3>
                      </div>
                    </div>

                    <div className="lxp-prueba-stack">
                      <div
                        className="lxp-status-toggle-block"
                        style={{ cursor: savingChecklistKey === "confesional_expresa" ? "wait" : "pointer" }}
                        onClick={() => handleToggleChecklist("confesional_expresa")}
                      >
                        <div className="lxp-v lxp-prueba-text">Reconocimiento directo de hechos</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <Switch
                            checked={tieneConfesionalExpresa}
                            loading={savingChecklistKey === "confesional_expresa"}
                            onChange={() => handleToggleChecklist("confesional_expresa")}
                            onClick={(_, e) => e.stopPropagation()}
                          />
                          <span className={`lxp-status-badge ${tieneConfesionalExpresa ? "is-on" : "is-off"}`}>
                            <span className="material-symbols-outlined">
                              {tieneConfesionalExpresa ? "check_circle" : "remove_circle"}
                            </span>
                            {tieneConfesionalExpresa ? "Incluida" : "No incluida"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </section>
                  </div>

                  {(checklistDemandadaGlobal?.confesional || confesionalesDemandada.length > 0) && (
                  <section className="lxp-prueba-card">
                    <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                          <i className="fa-solid fa-person-circle-question" />
                        </div>
                        <h3 className="lxp-section-title">
                          Confesional Demandada (Empresa Vinculada){" "}
                          <span className="lxp-section-count">({confesionalesDemandada.length})</span>
                        </h3>
                      </div>
                      {!editingConfesionalDemandadaCard && (
                        <Button onClick={startEditConfesionalDemandadaCard} className="laboral-exp-btn-clean">
                          Editar
                        </Button>
                      )}
                    </div>

                    {editingConfesionalDemandadaCard ? (
                      <div className="laboral-exp-edit-form">
                        {editingConfesionalDemandadaForm.map((item, idx) => (
                          <div key={item._id} className="lxp-edit-row-card">
                            <div className="lxp-edit-row-head">
                              <span>Confesional demandada {idx + 1}</span>
                              <Button
                                size="small"
                                className="laboral-exp-btn-clean"
                                onClick={() =>
                                  setEditingConfesionalDemandadaForm((rows) =>
                                    rows.filter((r) => r._id !== item._id)
                                  )
                                }
                              >
                                Quitar
                              </Button>
                            </div>
                            <div className="laboral-exp-edit-field">
                              <span className="laboral-exp-edit-label">Razón social demandada</span>
                              <Select
                                showSearch
                                optionFilterProp="label"
                                style={{ width: "100%" }}
                                placeholder="Selecciona la razón social"
                                options={razonesSocialesDemandadaOptions}
                                value={item.id_razon_social ?? undefined}
                                onChange={(v) =>
                                  setEditingConfesionalDemandadaForm((rows) =>
                                    rows.map((r) => (r._id === item._id ? { ...r, id_razon_social: v ?? null } : r))
                                  )
                                }
                              />
                            </div>
                          </div>
                        ))}

                        <Button
                          type="dashed"
                          block
                          icon={<i className="fa-solid fa-plus" />}
                          onClick={() =>
                            setEditingConfesionalDemandadaForm((rows) => [
                              ...rows,
                              { _id: genRowId(), id_razon_social: null },
                            ])
                          }
                        >
                          Agregar confesional demandada
                        </Button>

                        <div className="laboral-exp-edit-actions">
                          <Button
                            onClick={() => setEditingConfesionalDemandadaCard(false)}
                            className="laboral-exp-btn-clean"
                          >
                            Cancelar
                          </Button>
                          <HappyProvider>
                            <Button
                              type="primary"
                              loading={savingConfesionalDemandadaCard}
                              onClick={handleSaveConfesionalDemandadaCard}
                              className="laboral-exp-btn-save"
                              icon={<i className="fa-solid fa-floppy-disk" />}
                            >
                              Guardar
                            </Button>
                          </HappyProvider>
                        </div>
                      </div>
                    ) : confesionalesDemandada.length > 0 ? (
                      <div className="lxp-prueba-stack">
                        {confesionalesDemandada.map((item, idx) => (
                          <div key={item?.id || `${item?.empresa_nombre}-${idx}`} className="lxp-prueba-block">
                            <div className="lxp-prueba-block-title">
                              Confesional Demandada — {item?.razon_social || item?.empresa_nombre || "—"}
                            </div>
                            <div className="lxp-prueba-grid lxp-prueba-grid--2">
                              <div className="lxp-kv">
                                <div className="lxp-k">Empresa</div>
                                <div className="lxp-v">{item?.empresa_nombre || "—"}</div>
                              </div>
                              <div className="lxp-kv">
                                <div className="lxp-k">Razón social</div>
                                <div className="lxp-v">{item?.razon_social || "—"}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="lxp-empty-inline">Sin confesional demandada</div>
                    )}
                  </section>
                  )}

                  <div className="lxp-docs-grid-3">
                    <section className="lxp-prueba-card">
                      <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                            <i className="fa-solid fa-file-circle-check" />
                          </div>
                          <h3 className="lxp-section-title">Documental Pública</h3>
                        </div>
                        {!editingDocPublicaCard && (
                          <Button onClick={startEditDocPublicaCard} className="laboral-exp-btn-clean">
                            Editar
                          </Button>
                        )}
                      </div>

                      {editingDocPublicaCard ? (
                        <div className="laboral-exp-edit-form">
                          {editingDocPublicaForm.map((doc, idx) => (
                            <div key={doc._id} className="lxp-edit-row-card">
                              <div className="lxp-edit-row-head">
                                <span>Documento {idx + 1}</span>
                                <Button
                                  size="small"
                                  className="laboral-exp-btn-clean"
                                  onClick={() =>
                                    setEditingDocPublicaForm((rows) => rows.filter((r) => r._id !== doc._id))
                                  }
                                >
                                  Quitar
                                </Button>
                              </div>
                              <div className="laboral-exp-edit-grid">
                                <div className="laboral-exp-edit-field">
                                  <span className="laboral-exp-edit-label">Nombre</span>
                                  <Input
                                    value={doc.nombre}
                                    onChange={(e) =>
                                      setEditingDocPublicaForm((rows) =>
                                        rows.map((r) => (r._id === doc._id ? { ...r, nombre: e.target.value } : r))
                                      )
                                    }
                                  />
                                </div>
                                <div className="laboral-exp-edit-field">
                                  <span className="laboral-exp-edit-label">Fecha</span>
                                  <ScrollDatePicker
                                    style={{ width: "100%" }}
                                    format="DD/MM/YYYY"
                                    value={doc.fecha}
                                    onChange={(v) =>
                                      setEditingDocPublicaForm((rows) =>
                                        rows.map((r) => (r._id === doc._id ? { ...r, fecha: v } : r))
                                      )
                                    }
                                  />
                                </div>
                                <div className="laboral-exp-edit-field" style={{ gridColumn: "1 / -1" }}>
                                  <span className="laboral-exp-edit-label">Descripción</span>
                                  <Input.TextArea
                                    rows={2}
                                    value={doc.descripcion}
                                    onChange={(e) =>
                                      setEditingDocPublicaForm((rows) =>
                                        rows.map((r) => (r._id === doc._id ? { ...r, descripcion: e.target.value } : r))
                                      )
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          ))}

                          <Button
                            type="dashed"
                            block
                            icon={<i className="fa-solid fa-plus" />}
                            onClick={() =>
                              setEditingDocPublicaForm((rows) => [
                                ...rows,
                                { _id: genRowId(), nombre: "", descripcion: "", fecha: null },
                              ])
                            }
                          >
                            Agregar documento
                          </Button>

                          <div className="laboral-exp-edit-actions">
                            <Button onClick={() => setEditingDocPublicaCard(false)} className="laboral-exp-btn-clean">
                              Cancelar
                            </Button>
                            <HappyProvider>
                              <Button
                                type="primary"
                                loading={savingDocPublicaCard}
                                onClick={handleSaveDocPublicaCard}
                                className="laboral-exp-btn-save"
                                icon={<i className="fa-solid fa-floppy-disk" />}
                              >
                                Guardar
                              </Button>
                            </HappyProvider>
                          </div>
                        </div>
                      ) : (
                        <div className="lxp-doc-list">
                          {documentosPublicosActora.length ? (
                            documentosPublicosActora.map((doc) => (
                              <div key={doc?.id || doc?.nombre} className="lxp-doc-item">
                                <span className="material-symbols-outlined lxp-doc-icon">description</span>
                                <div>
                                  <div className="lxp-doc-name">{doc?.nombre || "—"}</div>
                                  <div className="lxp-doc-desc">{doc?.descripcion || "—"}</div>
                                  <div className="lxp-doc-date">{fmtShort(doc?.fecha)}</div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="lxp-empty-inline">Sin documentos públicos</div>
                          )}
                        </div>
                      )}
                    </section>

                    <section className="lxp-prueba-card">
                      <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                            <i className="fa-solid fa-file-lines" />
                          </div>
                          <h3 className="lxp-section-title">Documental Privada</h3>
                        </div>
                        {!editingDocPrivadaCard && (
                          <Button onClick={startEditDocPrivadaCard} className="laboral-exp-btn-clean">
                            Editar
                          </Button>
                        )}
                      </div>

                      {editingDocPrivadaCard ? (
                        <div className="laboral-exp-edit-form">
                          {editingDocPrivadaForm.map((doc, idx) => (
                            <div key={doc._id} className="lxp-edit-row-card">
                              <div className="lxp-edit-row-head">
                                <span>Documento {idx + 1}</span>
                                <Button
                                  size="small"
                                  className="laboral-exp-btn-clean"
                                  onClick={() =>
                                    setEditingDocPrivadaForm((rows) => rows.filter((r) => r._id !== doc._id))
                                  }
                                >
                                  Quitar
                                </Button>
                              </div>
                              <div className="laboral-exp-edit-grid">
                                <div className="laboral-exp-edit-field">
                                  <span className="laboral-exp-edit-label">Nombre</span>
                                  <Input
                                    value={doc.nombre}
                                    onChange={(e) =>
                                      setEditingDocPrivadaForm((rows) =>
                                        rows.map((r) => (r._id === doc._id ? { ...r, nombre: e.target.value } : r))
                                      )
                                    }
                                  />
                                </div>
                                <div className="laboral-exp-edit-field">
                                  <span className="laboral-exp-edit-label">Fecha</span>
                                  <ScrollDatePicker
                                    style={{ width: "100%" }}
                                    format="DD/MM/YYYY"
                                    value={doc.fecha}
                                    onChange={(v) =>
                                      setEditingDocPrivadaForm((rows) =>
                                        rows.map((r) => (r._id === doc._id ? { ...r, fecha: v } : r))
                                      )
                                    }
                                  />
                                </div>
                                <div className="laboral-exp-edit-field" style={{ gridColumn: "1 / -1" }}>
                                  <span className="laboral-exp-edit-label">Descripción</span>
                                  <Input.TextArea
                                    rows={2}
                                    value={doc.descripcion}
                                    onChange={(e) =>
                                      setEditingDocPrivadaForm((rows) =>
                                        rows.map((r) => (r._id === doc._id ? { ...r, descripcion: e.target.value } : r))
                                      )
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          ))}

                          <Button
                            type="dashed"
                            block
                            icon={<i className="fa-solid fa-plus" />}
                            onClick={() =>
                              setEditingDocPrivadaForm((rows) => [
                                ...rows,
                                { _id: genRowId(), nombre: "", descripcion: "", fecha: null },
                              ])
                            }
                          >
                            Agregar documento
                          </Button>

                          <div className="laboral-exp-edit-actions">
                            <Button onClick={() => setEditingDocPrivadaCard(false)} className="laboral-exp-btn-clean">
                              Cancelar
                            </Button>
                            <HappyProvider>
                              <Button
                                type="primary"
                                loading={savingDocPrivadaCard}
                                onClick={handleSaveDocPrivadaCard}
                                className="laboral-exp-btn-save"
                                icon={<i className="fa-solid fa-floppy-disk" />}
                              >
                                Guardar
                              </Button>
                            </HappyProvider>
                          </div>
                        </div>
                      ) : (
                        <div className="lxp-doc-list">
                          {documentosPrivadosActora.length ? (
                            documentosPrivadosActora.map((doc) => (
                              <div key={doc?.id || doc?.nombre} className="lxp-doc-item">
                                <span className="material-symbols-outlined lxp-doc-icon">description</span>
                                <div>
                                  <div className="lxp-doc-name">{doc?.nombre || "—"}</div>
                                  <div className="lxp-doc-desc">{doc?.descripcion || "—"}</div>
                                  <div className="lxp-doc-date">{fmtShort(doc?.fecha)}</div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="lxp-empty-inline">Sin documentos privados</div>
                          )}
                        </div>
                      )}
                    </section>

                  <section className="lxp-prueba-card">
                    <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                          <i className="fa-solid fa-landmark" />
                        </div>
                        <h3 className="lxp-section-title">Informe de Autoridad</h3>
                      </div>
                      {!editingInformeAutoridadCard && (
                        <Button onClick={startEditInformeAutoridadCard} className="laboral-exp-btn-clean">
                          Editar
                        </Button>
                      )}
                    </div>

                    {editingInformeAutoridadCard ? (
                      <div className="laboral-exp-edit-form">
                        {editingInformeAutoridadForm.map((doc, idx) => (
                          <div key={doc._id} className="lxp-edit-row-card">
                            <div className="lxp-edit-row-head">
                              <span>Documento {idx + 1}</span>
                              <Button
                                size="small"
                                className="laboral-exp-btn-clean"
                                onClick={() =>
                                  setEditingInformeAutoridadForm((rows) => rows.filter((r) => r._id !== doc._id))
                                }
                              >
                                Quitar
                              </Button>
                            </div>
                            <div className="laboral-exp-edit-grid">
                              <div className="laboral-exp-edit-field">
                                <span className="laboral-exp-edit-label">Nombre</span>
                                <Input
                                  value={doc.nombre}
                                  onChange={(e) =>
                                    setEditingInformeAutoridadForm((rows) =>
                                      rows.map((r) => (r._id === doc._id ? { ...r, nombre: e.target.value } : r))
                                    )
                                  }
                                />
                              </div>
                              <div className="laboral-exp-edit-field">
                                <span className="laboral-exp-edit-label">Fecha</span>
                                <ScrollDatePicker
                                  style={{ width: "100%" }}
                                  format="DD/MM/YYYY"
                                  value={doc.fecha}
                                  onChange={(v) =>
                                    setEditingInformeAutoridadForm((rows) =>
                                      rows.map((r) => (r._id === doc._id ? { ...r, fecha: v } : r))
                                    )
                                  }
                                />
                              </div>
                              <div className="laboral-exp-edit-field" style={{ gridColumn: "1 / -1" }}>
                                <span className="laboral-exp-edit-label">Descripción</span>
                                <Input.TextArea
                                  rows={2}
                                  value={doc.descripcion}
                                  onChange={(e) =>
                                    setEditingInformeAutoridadForm((rows) =>
                                      rows.map((r) => (r._id === doc._id ? { ...r, descripcion: e.target.value } : r))
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        ))}

                        <Button
                          type="dashed"
                          block
                          icon={<i className="fa-solid fa-plus" />}
                          onClick={() =>
                            setEditingInformeAutoridadForm((rows) => [
                              ...rows,
                              { _id: genRowId(), nombre: "", descripcion: "", fecha: null },
                            ])
                          }
                        >
                          Agregar documento
                        </Button>

                        <div className="laboral-exp-edit-actions">
                          <Button
                            onClick={() => setEditingInformeAutoridadCard(false)}
                            className="laboral-exp-btn-clean"
                          >
                            Cancelar
                          </Button>
                          <HappyProvider>
                            <Button
                              type="primary"
                              loading={savingInformeAutoridadCard}
                              onClick={handleSaveInformeAutoridadCard}
                              className="laboral-exp-btn-save"
                              icon={<i className="fa-solid fa-floppy-disk" />}
                            >
                              Guardar
                            </Button>
                          </HappyProvider>
                        </div>
                      </div>
                    ) : (
                      <div className="lxp-doc-list">
                        {informesAutoridadActora.length > 0 ? (
                          informesAutoridadActora.map((doc, idx) => (
                            <div key={doc?.id || `${doc?.nombre}-${idx}`} className="lxp-doc-item">
                              <span className="material-symbols-outlined lxp-doc-icon">description</span>
                              <div>
                                <div className="lxp-doc-name">{doc?.nombre || "—"}</div>
                                <div className="lxp-doc-desc">{doc?.descripcion || "—"}</div>
                                <div className="lxp-doc-date">{fmtShort(doc?.fecha)}</div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="lxp-empty-inline">Sin informes de autoridad</div>
                        )}
                      </div>
                    )}
                  </section>
                  </div>

                  <div className="lxp-docs-grid-3">
                  <section className="lxp-prueba-card">
                    <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                          <i className="fa-solid fa-users" />
                        </div>
                        <h3 className="lxp-section-title">Testimonial</h3>
                      </div>
                      {!editingTestimonialCard && (
                        <Button onClick={startEditTestimonialCard} className="laboral-exp-btn-clean">
                          Editar
                        </Button>
                      )}
                    </div>

                    {editingTestimonialCard ? (
                      <div className="laboral-exp-edit-form">
                        {editingTestimonialForm.map((t, idx) => (
                          <div key={t._id} className="lxp-edit-row-card">
                            <div className="lxp-edit-row-head">
                              <span>Testigo {idx + 1}</span>
                              <Button
                                size="small"
                                className="laboral-exp-btn-clean"
                                onClick={() =>
                                  setEditingTestimonialForm((rows) => rows.filter((r) => r._id !== t._id))
                                }
                              >
                                Quitar
                              </Button>
                            </div>
                            <div className="laboral-exp-edit-grid">
                              <div className="laboral-exp-edit-field">
                                <span className="laboral-exp-edit-label">Nombre</span>
                                <Input
                                  value={t.nombre}
                                  onChange={(e) =>
                                    setEditingTestimonialForm((rows) =>
                                      rows.map((r) => (r._id === t._id ? { ...r, nombre: e.target.value } : r))
                                    )
                                  }
                                />
                              </div>
                              <div className="laboral-exp-edit-field">
                                <span className="laboral-exp-edit-label">Relación</span>
                                <Input
                                  value={t.relacion}
                                  onChange={(e) =>
                                    setEditingTestimonialForm((rows) =>
                                      rows.map((r) => (r._id === t._id ? { ...r, relacion: e.target.value } : r))
                                    )
                                  }
                                />
                              </div>
                              <div className="laboral-exp-edit-field">
                                <span className="laboral-exp-edit-label">Contacto</span>
                                <Input
                                  value={t.contacto}
                                  onChange={(e) =>
                                    setEditingTestimonialForm((rows) =>
                                      rows.map((r) => (r._id === t._id ? { ...r, contacto: e.target.value } : r))
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        ))}

                        <Button
                          type="dashed"
                          block
                          icon={<i className="fa-solid fa-plus" />}
                          onClick={() =>
                            setEditingTestimonialForm((rows) => [
                              ...rows,
                              { _id: genRowId(), nombre: "", relacion: "", contacto: "" },
                            ])
                          }
                        >
                          Agregar testigo
                        </Button>

                        <div className="laboral-exp-edit-actions">
                          <Button onClick={() => setEditingTestimonialCard(false)} className="laboral-exp-btn-clean">
                            Cancelar
                          </Button>
                          <HappyProvider>
                            <Button
                              type="primary"
                              loading={savingTestimonialCard}
                              onClick={handleSaveTestimonialCard}
                              className="laboral-exp-btn-save"
                              icon={<i className="fa-solid fa-floppy-disk" />}
                            >
                              Guardar
                            </Button>
                          </HappyProvider>
                        </div>
                      </div>
                    ) : testigosActora.length > 0 ? (
                      <div className="lxp-testigos-grid">
                        {testigosActora.map((testigo, idx) => (
                          <div key={testigo?.id || `${testigo?.nombre}-${idx}`} className="lxp-testigo-card">
                            <div className="lxp-testigo-avatar">
                              <span className="material-symbols-outlined">person</span>
                            </div>

                            <div className="lxp-testigo-body">
                              <div className="lxp-testigo-name">{testigo?.nombre || "—"}</div>

                              <div className="lxp-testigo-meta">
                                <div className="lxp-testigo-kv">
                                  <div className="lxp-testigo-k">Relación</div>
                                  <div className="lxp-testigo-v">{testigo?.relacion || "—"}</div>
                                </div>

                                <div className="lxp-testigo-kv">
                                  <div className="lxp-testigo-k">Contacto</div>
                                  <div className="lxp-testigo-v">{testigo?.contacto || "—"}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="lxp-empty-inline">Sin testigos registrados</div>
                    )}
                  </section>


                  <section className="lxp-prueba-card">
                    <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                          <i className="fa-solid fa-magnifying-glass-chart" />
                        </div>
                        <h3 className="lxp-section-title">Pericial</h3>
                      </div>
                      {!editingPericialCard && (
                        <Button onClick={startEditPericialCard} className="laboral-exp-btn-clean">
                          Editar
                        </Button>
                      )}
                    </div>

                    {editingPericialCard ? (
                      <div className="laboral-exp-edit-form">
                        {editingPericialForm.map((item, idx) => (
                          <div key={item._id} className="lxp-edit-row-card">
                            <div className="lxp-edit-row-head">
                              <span>Pericial {idx + 1}</span>
                              <Button
                                size="small"
                                className="laboral-exp-btn-clean"
                                onClick={() =>
                                  setEditingPericialForm((rows) => rows.filter((r) => r._id !== item._id))
                                }
                              >
                                Quitar
                              </Button>
                            </div>
                            <div className="laboral-exp-edit-grid">
                              <div className="laboral-exp-edit-field" style={{ gridColumn: "1 / -1" }}>
                                <span className="laboral-exp-edit-label">Tipo de pericial</span>
                                <Select
                                  mode="multiple"
                                  showSearch
                                  optionFilterProp="label"
                                  style={{ width: "100%" }}
                                  placeholder="Selecciona uno o varios tipos"
                                  options={PERICIAL_TIPO_OPTIONS}
                                  value={item.tipo}
                                  onChange={(v) =>
                                    setEditingPericialForm((rows) =>
                                      rows.map((r) => (r._id === item._id ? { ...r, tipo: v } : r))
                                    )
                                  }
                                />
                              </div>
                              <div className="laboral-exp-edit-field">
                                <span className="laboral-exp-edit-label">Perito propuesto</span>
                                <Input
                                  value={item.perito}
                                  onChange={(e) =>
                                    setEditingPericialForm((rows) =>
                                      rows.map((r) => (r._id === item._id ? { ...r, perito: e.target.value } : r))
                                    )
                                  }
                                />
                              </div>
                              <div className="laboral-exp-edit-field">
                                <span className="laboral-exp-edit-label">Objeto de la prueba</span>
                                <Input.TextArea
                                  rows={2}
                                  value={item.objeto}
                                  onChange={(e) =>
                                    setEditingPericialForm((rows) =>
                                      rows.map((r) => (r._id === item._id ? { ...r, objeto: e.target.value } : r))
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        ))}

                        <Button
                          type="dashed"
                          block
                          icon={<i className="fa-solid fa-plus" />}
                          onClick={() =>
                            setEditingPericialForm((rows) => [
                              ...rows,
                              { _id: genRowId(), tipo: [], perito: "", objeto: "" },
                            ])
                          }
                        >
                          Agregar pericial
                        </Button>

                        <div className="laboral-exp-edit-actions">
                          <Button onClick={() => setEditingPericialCard(false)} className="laboral-exp-btn-clean">
                            Cancelar
                          </Button>
                          <HappyProvider>
                            <Button
                              type="primary"
                              loading={savingPericialCard}
                              onClick={handleSavePericialCard}
                              className="laboral-exp-btn-save"
                              icon={<i className="fa-solid fa-floppy-disk" />}
                            >
                              Guardar
                            </Button>
                          </HappyProvider>
                        </div>
                      </div>
                    ) : pericialItemsActora.length > 0 ? (
                      <div className="lxp-prueba-stack">
                        {pericialItemsActora.map((item, idx) => (
                          <div key={item?.id || idx} className="lxp-prueba-block">
                            <div className="lxp-prueba-grid lxp-prueba-grid--3">
                              <div className="lxp-kv">
                                <div className="lxp-k">Tipo de pericial</div>
                                <div className="lxp-v">{formatTipoList(splitJoinedTipo(item?.tipo))}</div>
                              </div>
                              <div className="lxp-kv">
                                <div className="lxp-k">Perito propuesto</div>
                                <div className="lxp-v">{item?.perito || "—"}</div>
                              </div>
                              <div className="lxp-kv">
                                <div className="lxp-k">Objeto de la prueba</div>
                                <div className="lxp-v">{item?.objeto || "—"}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="lxp-empty-inline">Sin pericial registrada</div>
                    )}
                  </section>

                  <section className="lxp-prueba-card">
                    <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                          <i className="fa-solid fa-eye" />
                        </div>
                        <h3 className="lxp-section-title">Inspección Ocular</h3>
                      </div>
                      {!editingInspeccionCard && (
                        <Button onClick={startEditInspeccionCard} className="laboral-exp-btn-clean">
                          Editar
                        </Button>
                      )}
                    </div>

                    {editingInspeccionCard ? (
                      <div className="laboral-exp-edit-form">
                        {editingInspeccionForm.map((item, idx) => (
                          <div key={item._id} className="lxp-edit-row-card">
                            <div className="lxp-edit-row-head">
                              <span>Inspección {idx + 1}</span>
                              <Button
                                size="small"
                                className="laboral-exp-btn-clean"
                                onClick={() =>
                                  setEditingInspeccionForm((rows) => rows.filter((r) => r._id !== item._id))
                                }
                              >
                                Quitar
                              </Button>
                            </div>
                            <div className="laboral-exp-edit-grid">
                              <div className="laboral-exp-edit-field" style={{ gridColumn: "1 / -1" }}>
                                <span className="laboral-exp-edit-label">Tipo de inspección ocular</span>
                                <Select
                                  mode="multiple"
                                  showSearch
                                  optionFilterProp="label"
                                  style={{ width: "100%" }}
                                  placeholder="Selecciona uno o varios tipos"
                                  options={INSPECCION_OCULAR_TIPO_OPTIONS}
                                  value={item.tipo}
                                  onChange={(v) =>
                                    setEditingInspeccionForm((rows) =>
                                      rows.map((r) => (r._id === item._id ? { ...r, tipo: v } : r))
                                    )
                                  }
                                />
                              </div>
                              <div className="laboral-exp-edit-field">
                                <span className="laboral-exp-edit-label">Nombre</span>
                                <Input
                                  value={item.nombre}
                                  onChange={(e) =>
                                    setEditingInspeccionForm((rows) =>
                                      rows.map((r) => (r._id === item._id ? { ...r, nombre: e.target.value } : r))
                                    )
                                  }
                                />
                              </div>
                              <div className="laboral-exp-edit-field">
                                <span className="laboral-exp-edit-label">Objeto de la prueba</span>
                                <Input.TextArea
                                  rows={2}
                                  value={item.objeto}
                                  onChange={(e) =>
                                    setEditingInspeccionForm((rows) =>
                                      rows.map((r) => (r._id === item._id ? { ...r, objeto: e.target.value } : r))
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        ))}

                        <Button
                          type="dashed"
                          block
                          icon={<i className="fa-solid fa-plus" />}
                          onClick={() =>
                            setEditingInspeccionForm((rows) => [
                              ...rows,
                              { _id: genRowId(), tipo: [], nombre: "", objeto: "" },
                            ])
                          }
                        >
                          Agregar inspección
                        </Button>

                        <div className="laboral-exp-edit-actions">
                          <Button onClick={() => setEditingInspeccionCard(false)} className="laboral-exp-btn-clean">
                            Cancelar
                          </Button>
                          <HappyProvider>
                            <Button
                              type="primary"
                              loading={savingInspeccionCard}
                              onClick={handleSaveInspeccionCard}
                              className="laboral-exp-btn-save"
                              icon={<i className="fa-solid fa-floppy-disk" />}
                            >
                              Guardar
                            </Button>
                          </HappyProvider>
                        </div>
                      </div>
                    ) : inspeccionItemsActora.length > 0 ? (
                      <div className="lxp-prueba-stack">
                        {inspeccionItemsActora.map((item, idx) => (
                          <div key={item?.id || idx} className="lxp-prueba-block">
                            <div className="lxp-prueba-grid lxp-prueba-grid--2">
                              <div className="lxp-kv">
                                <div className="lxp-k">Tipo</div>
                                <div className="lxp-v">{formatTipoList(splitJoinedTipo(item?.tipo))}</div>
                              </div>
                              <div className="lxp-kv">
                                <div className="lxp-k">Nombre</div>
                                <div className="lxp-v">{item?.nombre || "—"}</div>
                              </div>
                            </div>
                            <div className="lxp-kv" style={{ marginTop: 12 }}>
                              <div className="lxp-k">Objeto</div>
                              <div className="lxp-v">{item?.objeto || "—"}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="lxp-empty-inline">Sin inspección ocular registrada</div>
                    )}
                  </section>
                  </div>

                  <div className="lxp-docs-grid-3">
                    <section className="lxp-prueba-card">
                      <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                            <i className="fa-solid fa-scale-balanced" />
                          </div>
                          <h3 className="lxp-section-title">Presuncional Legal y Humana</h3>
                        </div>
                      </div>

                      <div className="lxp-prueba-stack">
                        <div
                          className="lxp-status-toggle-block"
                          style={{ cursor: savingChecklistKey === "presuncional_legal_humana" ? "wait" : "pointer" }}
                          onClick={() => handleToggleChecklist("presuncional_legal_humana")}
                        >
                          <div className="lxp-v lxp-prueba-text">Deducciones extraídas de hechos</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <Switch
                              checked={tienePresuncionalActora}
                              loading={savingChecklistKey === "presuncional_legal_humana"}
                              onChange={() => handleToggleChecklist("presuncional_legal_humana")}
                              onClick={(_, e) => e.stopPropagation()}
                            />
                            <span className={`lxp-status-badge ${tienePresuncionalActora ? "is-on" : "is-off"}`}>
                              <span className="material-symbols-outlined">
                                {tienePresuncionalActora ? "check_circle" : "remove_circle"}
                              </span>
                              {tienePresuncionalActora ? "Incluida" : "No incluida"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </section>

                    <section className="lxp-prueba-card">
                      <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                            <i className="fa-solid fa-folder-tree" />
                          </div>
                          <h3 className="lxp-section-title">Instrumental de Actuaciones</h3>
                        </div>
                      </div>

                      <div className="lxp-prueba-stack">
                        <div
                          className="lxp-status-toggle-block"
                          style={{ cursor: savingChecklistKey === "instrumental_actuaciones" ? "wait" : "pointer" }}
                          onClick={() => handleToggleChecklist("instrumental_actuaciones")}
                        >
                          <div className="lxp-v lxp-prueba-text">Integrada formalmente al expediente</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <Switch
                              checked={tieneInstrumentalActora}
                              loading={savingChecklistKey === "instrumental_actuaciones"}
                              onChange={() => handleToggleChecklist("instrumental_actuaciones")}
                              onClick={(_, e) => e.stopPropagation()}
                            />
                            <span className={`lxp-status-badge ${tieneInstrumentalActora ? "is-on" : "is-off"}`}>
                              <span className="material-symbols-outlined">
                                {tieneInstrumentalActora ? "check_circle" : "remove_circle"}
                              </span>
                              {tieneInstrumentalActora ? "Incluida" : "No incluida"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </section>

                    <section className="lxp-prueba-card">
                      <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                            <i className="fa-solid fa-microchip" />
                          </div>
                          <h3 className="lxp-section-title">
                            Medios Electrónicos / Ciencia{" "}
                            <span className="lxp-section-count">({safeArr(mediosElectronicosActora?.tipos).length})</span>
                          </h3>
                        </div>
                        {!editingMediosCard && (
                          <Button onClick={startEditMediosCard} className="laboral-exp-btn-clean">
                            Editar
                          </Button>
                        )}
                      </div>

                      {editingMediosCard ? (
                        <div className="laboral-exp-edit-form">
                          <div className="laboral-exp-edit-grid">
                            <div className="laboral-exp-edit-field" style={{ gridColumn: "1 / -1" }}>
                              <span className="laboral-exp-edit-label">Tipo(s) de medio</span>
                              <Select
                                mode="multiple"
                                showSearch
                                optionFilterProp="label"
                                style={{ width: "100%" }}
                                placeholder="Selecciona uno o varios medios"
                                options={MEDIOS_ELECTRONICOS_OPTIONS}
                                value={editingMediosForm.tipos}
                                onChange={(v) => setEditingMediosForm((f) => ({ ...f, tipos: v }))}
                              />
                            </div>
                            <div className="laboral-exp-edit-field" style={{ gridColumn: "1 / -1" }}>
                              <span className="laboral-exp-edit-label">Descripción</span>
                              <Input.TextArea
                                rows={2}
                                value={editingMediosForm.descripcion}
                                onChange={(e) =>
                                  setEditingMediosForm((f) => ({ ...f, descripcion: e.target.value }))
                                }
                              />
                            </div>
                          </div>

                          <div className="laboral-exp-edit-actions">
                            <Button onClick={() => setEditingMediosCard(false)} className="laboral-exp-btn-clean">
                              Cancelar
                            </Button>
                            <HappyProvider>
                              <Button
                                type="primary"
                                loading={savingMediosCard}
                                onClick={handleSaveMediosCard}
                                className="laboral-exp-btn-save"
                                icon={<i className="fa-solid fa-floppy-disk" />}
                              >
                                Guardar
                              </Button>
                            </HappyProvider>
                          </div>
                        </div>
                      ) : safeArr(mediosElectronicosActora?.tipos).length > 0 || mediosElectronicosActora?.descripcion ? (
                        <div className="lxp-prueba-stack">
                          <div className="lxp-prueba-block">
                            <div className="lxp-kv">
                              <div className="lxp-k">Tipo(s) de medio</div>
                              <div className="lxp-v">{safeArr(mediosElectronicosActora?.tipos).join(", ") || "—"}</div>
                            </div>
                            <div className="lxp-kv" style={{ marginTop: 12 }}>
                              <div className="lxp-k">Descripción</div>
                              <div className="lxp-v lxp-prueba-text">{mediosElectronicosActora?.descripcion || "—"}</div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="lxp-empty-inline">Sin medios electrónicos registrados</div>
                      )}
                    </section>
                  </div>
                </div>
              </>
            )}

            {activeMainTab === "2" && (
              <div style={{ marginTop: 18 }}>
                <TabExpedienteActuaciones
                  expedienteId={idExpediente}
                  onIrATerminacion={() => setActiveMainTab("6")}
                  caseInfo={caseInfoConcentrado}
                  pruebasActora={pruebasActoraFlat}
                  onDatosActualizados={fetchDetalle}
                  antecedentes={{
                    ultimoDiaLaborado: fechaUltimoDiaLaborado,
                    citatorioCCL: fechaEmisionDemanda,
                    constanciaNoConciliacion: fechaNoConciliacionDemanda,
                    presentacionDemanda: fechaPresentacionDemanda,
                    notificacionDemanda: fechaNotificacionDemanda,
                    fechaAudienciaConciliatoria: etapa?.fecha_audiencia_conciliatoria || null,
                    demanda: etapa?.citatorio?.url
                      ? { url: etapa.citatorio.url, filename: etapa.citatorio.filename || "Demanda" }
                      : null,
                  }}
                />
              </div>
            )}

            {activeMainTab === "4" && (
              <div style={{ marginTop: 18 }}>
                <TabCuantificaciones
                  detalle={detalle}
                  expedienteId={idExpediente}
                  onExportar={() => notify.info({ message: "Exportar", description: "Conecta exportación a Excel aquí." })}
                  onNuevoConcepto={() => notify.info({ message: "Nuevo concepto", description: "Conecta tu modal/form aquí." })}
                  onEditarConcepto={(it) => notify.info({ message: "Editar", description: it?.title || "Concepto" })}
                />
              </div>
            )}
            {activeMainTab === "6" && (
              <div style={{ marginTop: 18 }}>
                <TabTerminacion expedienteId={idExpediente} onDatosActualizados={fetchDetalle} />
              </div>
            )}
            {activeMainTab === "7" && <MovimientosCard items={auditItems} />}
          </div>


        </div>

        <div style={{ height: 40 }} />
      </div>

      {/* ===== Carga rápida (IA): pegar JSON, revisar y guardar ===== */}
      <Modal
        open={cargaRapidaOpen}
        onCancel={cerrarCargaRapida}
        footer={null}
        width={660}
        centered
        destroyOnClose
        title={null}
        closeIcon={<span style={{ color: "#0F172A", fontSize: 16, lineHeight: 1 }}>✕</span>}
      >
        {!cargaRapidaReview ? (
          <div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 26, color: "var(--lxp-ea-accent, #0A539A)" }}>auto_awesome</span>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>Carga rápida con IA</div>
                <div style={{ fontSize: 12.5, color: "#64748B", marginTop: 2 }}>
                  Pega aquí el JSON que te devolvió la IA al analizar la demanda. Antes de guardar nada
                  te voy a mostrar exactamente qué se va a actualizar en el expediente.
                </div>
              </div>
            </div>

            <Input.TextArea
              rows={14}
              value={cargaRapidaText}
              onChange={(e) => { setCargaRapidaText(e.target.value); if (cargaRapidaError) setCargaRapidaError(""); }}
              placeholder={'{\n  "datos_generales": { "numero_expediente": "79/2026", ... },\n  "plazos": { ... },\n  "condiciones_trabajo": { ... },\n  "pruebas_actora": [\n    { "tipo": "confesional", "persona_confesar": "..." },\n    { "tipo": "testimonial", "nombre": "...", "relacion": "...", "contacto": "..." },\n    { "tipo": "documental_publica", "nombre": "...", "descripcion": "...", "fecha": "..." }\n  ],\n  ...\n}'}
              style={{ fontFamily: "monospace", fontSize: 12.5 }}
              status={cargaRapidaError ? "error" : undefined}
            />
            {cargaRapidaError && (
              <div style={{ color: "#DC2626", fontSize: 12.5, marginTop: 6 }}>{cargaRapidaError}</div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
              <Button onClick={cerrarCargaRapida}>Cancelar</Button>
              <Button type="primary" disabled={!cargaRapidaText.trim()} onClick={parseCargaRapida}>
                Revisar datos extraídos
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 26, color: "var(--lxp-ea-accent, #0A539A)" }}>fact_check</span>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>¿Guardamos esta información?</div>
                <div style={{ fontSize: 12.5, color: "#64748B", marginTop: 2 }}>
                  Esto es lo que se va a actualizar en el expediente. Si algo no se ve bien, cancela y
                  corrige el JSON — una vez que aceptes, se guarda directo en la base de datos.
                </div>
              </div>
            </div>

            <div style={{ maxHeight: 360, overflowY: "auto", border: "1px solid #E4E8EF", borderRadius: 10 }}>
              {cargaRapidaReview.cambios.map((c, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex", gap: 14, padding: "10px 14px",
                    borderBottom: i < cargaRapidaReview.cambios.length - 1 ? "1px solid #F1F5F9" : "none",
                  }}
                >
                  <div style={{ width: 210, flexShrink: 0, fontSize: 12, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: ".03em" }}>
                    {c.label}
                  </div>
                  <div style={{ fontSize: 13, color: "#0F172A", wordBreak: "break-word" }}>
                    {String(c.valor)}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
              <Button onClick={() => setCargaRapidaReview(null)} disabled={cargaRapidaSaving}>Volver</Button>
              <Button onClick={cerrarCargaRapida} disabled={cargaRapidaSaving}>Cancelar</Button>
              <Button type="primary" loading={cargaRapidaSaving} onClick={confirmarCargaRapida}>
                Aceptar y guardar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
